// The client half of the two-editor safety contract (crew K8 — van
// connectivity). Rules of the road, enforced HERE:
//
// - NO optimistic UI. A mutation is pending until the server acks; the
//   screen shows it pending and applies the SERVER's row on ack.
// - Every mutation carries one Idempotency-Key per INTENT (uuid), reused
//   across retries — a retry can never double-write.
// - Every fetch has a hard timeout (kimi LB-1): a stalled van connection
//   must not pin a mutation in 'sending' forever — the flush pass only
//   retries 'queued', so a hung fetch would never recover.
// - Network failure / 5xx / timeout → the mutation sits in a VISIBLE retry
//   queue (localStorage-persistent: survives closing the browser in a dead
//   zone) and retries on reconnect + interval. A clean that didn't ack is
//   a clean not yet logged — the queue says so.
// - 409 version conflict → NOT retried (the other owner edited the row);
//   it parks in the queue as 'conflict' until the user reloads the data.
// - 400/422 → the edit itself is invalid; surfaced to the caller, not
//   queued (retrying can't help). If a QUEUED entry is later rejected
//   with 4xx (state moved on), it parks as 'failed' — red, explicit
//   dismissal. Dropping it silently would read as "it went through"
//   (codex CRITICAL-2 / kimi LB-4).
// - Sequential same-row edits (kimi LB-2) keep working WITHOUT breaking
//   request hashing: an edit queued behind an unresolved edit to the same
//   method+path is sent only after the earlier one resolves, with the
//   acked version chained into it BEFORE its first send. An intent that
//   has already been attempted retries byte-identical under its key, ever.
//
// Queue persistence (v2) is one localStorage record PER INTENT, not one
// array for the whole queue. v1's single key meant two tabs that both
// loaded an empty queue each wrote their own one-entry array on their
// first offline edit — and the second write erased the first tab's intent
// (confirmed lost update). Per-intent records mean adding an intent can
// never overwrite another tab's: this tab writes only records IT changed
// (diffed against the last synced state), removals remove exactly one
// record, and 'storage' events keep every tab's memory aligned with what
// is actually persisted — a tab can no longer resurrect an intent another
// tab already resolved from stale in-memory copy.

let switchDraftTracking = false
export function setSwitchDraftTracking(on: boolean): void { switchDraftTracking = on }
function draftEvent(type: string, key: string): void {
  if (switchDraftTracking) window.dispatchEvent(new CustomEvent(type, { detail: { key } }))
}
export interface QueuedMutation {
  authorEmail?: string
  key: string
  label: string
  method: 'POST' | 'PATCH' | 'DELETE'
  path: string
  body: unknown
  // 'signed-out' is a HOLD, not a rejection: the request never reached the
  // API, Access answered its login page instead. Retrying is pointless until
  // the session is renewed, and dropping it would read as saved — so it waits,
  // visibly, and goes back to 'queued' the moment a read succeeds again.
  // 'parked' is the business hold (multi-business contract §6): the server
  // said the account signed in right now is not the business this change was
  // made for (409 business_mismatch, or a 403 saying the sign-in has no
  // business or a paused one). Kept, never sent, not a version conflict and
  // not dismissible as one; it goes back to 'queued' only once its own
  // business is confirmed signed in again. On disk it is written as 'queued'
  // plus `"parked":true` (see storedForm): yesterday's app knows no 'parked'
  // and would read the record as corrupt, so after a rollback it must find an
  // ordinary waiting change it can send.
  status: 'sending' | 'queued' | 'conflict' | 'failed' | 'signed-out' | 'parked'
  error?: string
  createdAt: number
  // The business this change was made for. It rides on every send as
  // X-Roundly-Business, so the server refuses it under any other sign-in.
  // Records written by yesterday's app carry none: only business 1 existed
  // then, and that is what they are read back as. null = a change by an
  // account with no business (admin, or someone setting one up): it may only
  // go to a route that belongs to no business.
  businessId: number | null
  // v2 cross-tab coordination: which tab is mid-send of this intent. A
  // 'sending' record with a FRESH foreign claim is left alone by other
  // tabs (they must not send the same intent at the same moment); a STALE
  // claim (the sender's fetch hard-times-out at SEND_TIMEOUT_MS) is taken
  // over — the idempotency key makes the takeover safe either way.
  claim?: { by: string; at: number } | null
  // Has this intent ever been handed to fetch? Once attempted, the exact
  // body under its idempotency key is FROZEN: the server binds the key to
  // a request hash, and a retried body that differs 409s. Version chaining
  // (chainVersion) therefore only ever touches never-attempted intents —
  // an attempted-but-unacknowledged intent must retry byte-identical, and
  // if its stale version 409s, that conflict is answered honestly by the
  // user reloading, never by quietly rewriting the request.
  attempted?: boolean
  // Several jobs (0042, PLAN 2c, v3 Astra r2): true when the screen that MADE this change knows about a customer's
  // several jobs. Saved on the record when the change is made, never added when it is sent, so a change an older
  // screen queued stays an older screen's change through reload, update and retry; only records carrying it send
  // X-Roundly-Jobs: 1. Older app code reading a record with the field treats it as an ordinary waiting change.
  jobsAware?: boolean
  // Money brought from Squeegee (0043, PLAN section 2): true when the app build that MADE this change records cash
  // amounts ("Paid cash" sends what was taken, and the server answers with what is still to pay). Saved on the record
  // when the change is made, like jobsAware, so a change an older build queued keeps saying it came from an older
  // build: the server then asks the owner to confirm the cash instead of guessing it.
  moneyAware?: boolean
  // A change refused because someone else changed the same box first (same-time saves, 15 Oct 2026): each box, what
  // it says now and who changed it, as the server answered. The "didn't save" list shows them. Older app code ignores it.
  theirs?: TheirChange[]
  // A tap made on the lock screen card (docs/plans/lock-screen/REAL-CARD.md 3a): the key of the change the card's
  // picture of the job rested on, or null for none. Only that change's answer may chain a version into it.
  // Older app code ignores it.
  cardAfter?: string | null
}

// One box someone else changed first: the field, its value now, and who changed it (their name, an email, or null).
export interface TheirChange {
  field: string
  value: unknown
  by: string | null
}

// What a change was made on (same-time saves, 15 Oct 2026): for each box it changes, the value the screen showed when
// the person made it, taken at the tap and sent with the change as `base`. When someone else has changed the record
// since, the server still saves it if none of these boxes changed under it (and the boxes are ones that may: never
// money, Done or the day). Only fields the change sends are taken; a box the row does not carry is left out (that box
// then stays exact, as before).
export function withBase<T extends Record<string, unknown>>(row: object, patch: T): T & { base: Record<string, unknown> } {
  const from = row as Record<string, unknown>
  const base: Record<string, unknown> = {}
  for (const k of Object.keys(patch)) {
    if (k === 'version' || k === 'base') continue
    if (Object.prototype.hasOwnProperty.call(from, k)) base[k] = from[k] ?? null
  }
  return { ...patch, base }
}

// One wording for an expired Cloudflare Access session, shared by the queue
// chip and the load-error panel so the phone says the same thing whichever
// half of the app noticed first.
export const SIGNED_OUT_MESSAGE = 'Your sign-in has expired. Tap to sign in again'
// The reason a queued intent is held: an earlier edit to the SAME row has not
// resolved yet. Exported so the chip can say that instead of "waiting for
// signal", which claims a connectivity problem that is not happening.
export const BEHIND_EARLIER = 'behind an earlier change to this row'
// A change held for ITS business (parked): the chip and the entry say the
// same words.
export const WAITING_FOR_ACCOUNT = 'waiting for another account to sign in'
// A change or read asked for when this phone has no business to make it for
// (a paused business, or a sign-in with none): refused on the phone, never
// sent, and never held for whoever signs in next.
const NO_BUSINESS_MESSAGE = 'Not saved: this sign-in has no business on Roundly'
const NO_BUSINESS_READ = 'this sign-in has no business on Roundly'
// A read answered after the phone switched to another account belongs to the
// account signed in before, so it is dropped rather than shown or cached.
const IDENTITY_CHANGED_MESSAGE = 'signed in as a different account now'
// A change asked for from screens drawn for an account that is no longer the
// one signed in (see setScreensStale).
const ACCOUNT_CHANGED_MESSAGE = "You're signed in as another account now. Sign back in as the first one, or reload the page."

const LEGACY_KEY = 'dropby-round-queue-v1'
const RECORD_PREFIX = 'dropby-round-queue-v2:'
// Keys this v2 install has ever known (migrated from v1 or created here).
// Only consulted by migration: an old-version tab still writing whole-queue
// v1 arrays can list an intent this install already resolved — its record
// is gone because it was ACKED/dismissed, not because it was never moved.
const SEEN_KEY = 'dropby-round-queue-v2-seen'
const SEEN_MAX = 1000
const RETRY_MS = 15_000
const SEND_TIMEOUT_MS = 20_000
// Uploads (voice notes) get their own, LONGER timeout: the server spends up
// to 30 s waiting on the transcription before it answers, so the ordinary
// 20 s mutation timeout would give up first — the phone would abandon a send
// that was about to succeed and re-send it. 40 s clears the server's worst
// case with room to spare.
export const UPLOAD_TIMEOUT_MS = 40_000
// A claim older than this cannot be live: the claiming tab's own fetch
// timed out long ago, so the intent is takeable.
const CLAIM_STALE_MS = SEND_TIMEOUT_MS + 10_000
const TAB_ID = crypto.randomUUID()

// ---- identity partition -----------------------------------------------------
// One phone, several accounts: the owner and a (test) worker of one business
// share a browser, and since multi-business (contract §6) so can the owners
// and workers of OTHER businesses. Cached reads and queued edits left under
// SHARED keys would cross over: one account's app would show another's cached
// data, and worse, its queued edits would SEND under the other's sign-in.
// Storage is therefore namespaced by who is signed in:
//   - business 1 owner  → the pre-existing (legacy) keys exactly as before, so
//                         nothing already queued or cached is lost, and a phone
//                         rolled back to yesterday's app finds all of it where
//                         it left it;
//   - business 1 worker → the same keys under '<short-hash-of-email>:', as
//                         before, and NEVER the legacy ones;
//   - any other business → 'b<id>:', plus the worker hash for its workers;
//   - admin, or a sign-in with no business yet → 'a:<hash>:' / 'n:<hash>:'.
//                         They have no business, so no business's data is
//                         ever requested for them;
//   - unknown (before /api/me answers) → NOTHING is loaded from the queue and
//                         nothing may flush or send: sending queued edits
//                         before knowing who signed in is the exact leak.
// Queued work is never DELETED on an identity switch, only re-hidden; cached
// READS of the previous identity are dropped (fix c).
//
// Every request for a business's data carries 'X-Roundly-Business: <id>', the
// business it was MADE for (a queued change keeps its own), and the server
// refuses it under any other sign-in. That header is the boundary; the rest
// keeps the phone's own picture straight. An identity GENERATION counts the
// identity changes in this tab: an answer, cache write or queue
// acknowledgement whose request started under an older generation is
// discarded, so an old request finishing can never write into, or remove
// from, the namespace that is active now.
const IDENTITY_KEY = 'dropby-round-identity-v1'

interface Partition {
  // Storage namespace: '' = the legacy keys (business 1 owner); null = none.
  ns: string | null
  // The business requests are made for; null = no business, no tenant data.
  businessId: number | null
  gen: number
}

let signedInEmail: string | null = null
const EMAIL_NAMESPACES = 'roundly:email-namespaces:v1'
function knownNamespaces(): Record<string, string[]> {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(EMAIL_NAMESPACES) ?? '{}')
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
    return Object.fromEntries(Object.entries(value).filter(([, list]) => Array.isArray(list) && list.every((ns) => typeof ns === 'string')))
  } catch { return {} }
}
let partition: Partition = { ns: null, businessId: null, gen: 0 }
// Has /api/me given this page any answer yet? Before the first one, a
// mutation is held under the legacy keys exactly where yesterday's app held
// it (no screen can run then; this is the boot-order guarantee, not a path
// anyone takes). Once an answer has come, a phone with no business to act for
// (a paused one) holds and sends nothing at all.
let identified = false
// The identity record this tab wrote last: another tab writing a different
// one is the cross-tab broadcast of an identity change.
let ownIdentityRecord: string | null = null

// ---- no signal at start-up (docs/plans/offline/PLAN-v2.md §2, 2026-10-01) ----------
// When Roundly opens with no signal, me.ts restores the last person signed in on this
// phone (offline-identity.ts, at most 3 days old) in LOCAL-ONLY mode: their saved copies
// may be read and their work recorded into the queue and media drafts, but NOTHING is
// sent and nothing is asked of the server, until /api/me answers online for the same
// person. Then setIdentityPartition() ends local-only mode and flushes as usual.
let localOnlyMode = false

export function isLocalOnly(): boolean {
  return localOnlyMode
}

const NO_SIGNAL = 'no connection'

// A namespace, not a secret: enough to keep two accounts' storage apart.
export function shortHash(input: string): string {
  let h = 5381
  for (let i = 0; i < input.length; i++) h = ((h << 5) + h + input.charCodeAt(i)) | 0
  return (h >>> 0).toString(16).padStart(8, '0')
}

export interface PartitionIdentity {
  role: 'owner' | 'worker' | 'admin' | 'none'
  email: string
  // The business this sign-in belongs to. Absent on an owner or worker means
  // business 1: yesterday's server answered without one, and it only had
  // Dropby.
  businessId?: number | null
}

function describeIdentity(identity: PartitionIdentity): { role: string; ns: string; businessId: number | null } {
  const hash = shortHash(identity.email)
  if (identity.role === 'admin') return { role: 'admin', ns: `a:${hash}:`, businessId: null }
  if (identity.role === 'none') return { role: 'none', ns: `n:${hash}:`, businessId: null }
  const businessId = isPositiveSafeInt(identity.businessId) ? identity.businessId : 1
  const base = businessId === 1 ? '' : `b${businessId}:`
  return { role: identity.role, ns: base + (identity.role === 'worker' ? `${hash}:` : ''), businessId }
}

// The queue in force: this identity's namespace, or before the first answer
// the legacy records (yesterday's pre-identity hold). null = no queue at all.
function queueNamespace(): string | null {
  if (partition.ns !== null) return partition.ns
  return identified ? null : ''
}

// The business a change made NOW belongs to.
function queueBusiness(): number | null {
  return identified ? partition.businessId : 1
}

function queueRecordPrefix(): string | null {
  const ns = queueNamespace()
  return ns === null ? null : RECORD_PREFIX + ns
}

// A queue record of THIS namespace: the legacy prefix is a prefix of every
// other namespace's keys too, and intent keys (UUIDs) never contain ':', so a
// further ':' means the record is another account's.
function isOwnRecordKey(k: string): boolean {
  const prefix = queueRecordPrefix()
  return prefix !== null && k.startsWith(prefix) && !k.slice(prefix.length).includes(':')
}

function cacheStorageKey(key: string): string | null {
  return partition.ns === null ? null : CACHE_PREFIX + partition.ns + key
}

// The identity record keeps yesterday's exact shape, {role, hash}, where hash
// is the namespace without its last ':' (null for the legacy keys). A
// business-1 phone writes the very bytes yesterday's app wrote, and a phone
// rolled back to yesterday's app reads any record well enough to drop only
// the right cache block.
function identityRecord(role: string, ns: string): string {
  return JSON.stringify({ role, hash: ns === '' ? null : ns.slice(0, -1) })
}

// The namespace the stored record names; null when it names nobody (none
// stored, unreadable, or yesterday's record of a failed check).
function storedIdentityNs(): string | null {
  try {
    const raw = localStorage.getItem(IDENTITY_KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as { role?: unknown; hash?: unknown } | null
    if (!v || typeof v !== 'object' || typeof v.role !== 'string') return null
    if (v.hash === null || v.hash === undefined) return ''
    return typeof v.hash === 'string' && v.hash !== '' ? `${v.hash}:` : null
  } catch {
    return null
  }
}

// Called by loadMe() whenever /api/me gives a real answer, before any screen
// mounts, so every later read/write lands in the right namespace. `null` is
// an answer that leaves nobody to act for (a paused business, or a sign-in on
// no list that gave no email): nothing loads, nothing sends. A check that
// merely FAILED is not an answer and never reaches here: the identity in
// force stands, its record is not rewritten and no cache is dropped
// (yesterday's app dropped a worker's cache on every offline boot).
// Returns true when who is signed in actually changed.
export function setIdentityPartition(identity: PartitionIdentity | null, opts: { localOnly?: boolean } = {}): boolean {
  // Any real answer from the server ends local-only mode; a restore with no signal starts it.
  localOnlyMode = !!opts.localOnly && identity !== null
  const previousEmail = signedInEmail
  signedInEmail = identity?.email.trim().toLowerCase() ?? null
  const next = identity ? describeIdentity(identity) : null
  if (next && signedInEmail) {
    try {
      const all = knownNamespaces()
      all[signedInEmail] = [...new Set([...(all[signedInEmail] ?? []), next.ns])]
      localStorage.setItem(EMAIL_NAMESPACES, JSON.stringify(all))
    } catch { /* current namespace is still cleared on logout */ }
  }
  const nextNs = next ? next.ns : null
  const nextBusiness = next ? next.businessId : null
  const changed = !identified || previousEmail !== signedInEmail || partition.ns !== nextNs || partition.businessId !== nextBusiness
  if (!changed) {
    // The same account, confirmed again (a re-check on tab focus, or after a
    // mismatch that turned out not to be one). Nothing is reloaded, so this
    // tab's own sends in flight stay exactly as they are; changes parked for
    // THIS business may go again.
    if (nextNs !== null) {
      releaseParked()
      void flushQueue()
    }
    return false
  }
  // Last chance for the outgoing namespace to save what only this tab's
  // memory holds (a record write that failed earlier), under ITS keys; what
  // still cannot be saved is set aside for that account, not dropped.
  if (localOnly.size > 0 || pendingRemovals.size > 0) persistQueueChanges()
  setAsideUnsaved()
  identified = true
  // (c) Drop the PREVIOUS identity's cached reads so its stale window can't
  // be shown as if it were ours. Queued work belongs to its author and is
  // left in place, hidden.
  if (next) {
    const stored = storedIdentityNs()
    for (const old of new Set([stored, partition.ns])) {
      if (old !== null && old !== nextNs) dropCacheNamespace(old)
    }
  }
  partition = { ns: nextNs, businessId: nextBusiness, gen: partition.gen + 1 }
  if (next) {
    ownIdentityRecord = identityRecord(next.role, next.ns)
    try {
      localStorage.setItem(IDENTITY_KEY, ownIdentityRecord)
    } catch {
      // advisory only — the in-memory partition still governs this session
    }
  }
  // NOW this identity's queue may load (their namespace only) and flush.
  loadNamespaceQueue()
  for (const fn of listeners) fn()
  void flushQueue()
  return true
}

// The outgoing account's changes that only this tab's memory holds, and the
// record removals it still owes, kept for when that account is back.
function setAsideUnsaved(): void {
  const ns = queueNamespace()
  if (ns === null) return
  const entries = queue.filter((m) => localOnly.has(m.key))
  const removals = [...pendingRemovals]
  if (entries.length === 0 && removals.length === 0) return
  const earlier = heldInMemory.get(ns)
  heldInMemory.set(ns, {
    entries: [...(earlier?.entries ?? []), ...entries],
    removals: [...(earlier?.removals ?? []), ...removals],
  })
}

function loadNamespaceQueue(): void {
  signedOutAt = 0
  localOnly.clear()
  pendingRemovals.clear()
  foreignHere = 0
  if (partition.ns === null) {
    queue = []
    persisted = new Map()
    return
  }
  migrateLegacy() // only the business-1 owner namespace takes the shared v1 array
  const boot = scanRecords()
  // Loading a namespace follows a fresh /api/me for its business, so changes
  // parked for that business may go again.
  queue = orderQueue(boot.raw.map(normalizeLoaded).map(unpark))
  persisted = new Map(boot.raw.map((m) => [m.key, m]))
  foreignHere = boot.foreign
  if (boot.corrupt) storageCorrupt = true
  // This account's changes that were set aside in memory come back, and the
  // record writes and removals they were owed are tried again. A set-aside
  // change REPLACES any record of the same key on disk: that record is the
  // last copy storage accepted, and the one in memory is newer (a version
  // chained in after it, a send attempted since) — sending the disk copy
  // would go out on a stale version and conflict with the user's own edit.
  const held = heldInMemory.get(partition.ns)
  if (held) {
    const foreign = held.entries.filter((m) => m.authorEmail && m.authorEmail !== signedInEmail)
    if (foreign.length) heldInMemory.set(partition.ns, { entries: foreign, removals: [] })
    else heldInMemory.delete(partition.ns)
    for (const key of held.removals) pendingRemovals.add(key)
    const back = held.entries.filter((m) => !m.authorEmail || m.authorEmail === signedInEmail).map(normalizeLoaded).map(unpark)
    const replaced = new Set(back.map((m) => m.key))
    for (const m of back) localOnly.add(m.key)
    queue = orderQueue([...queue.filter((m) => !pendingRemovals.has(m.key) && !replaced.has(m.key)), ...back])
    persistQueueChanges()
  }
}

function unpark(m: QueuedMutation): QueuedMutation {
  return m.status === 'parked' ? { ...m, status: 'queued' as const, error: undefined, claim: null } : m
}

// A re-confirmation releases parked changes at most once per retry period:
// a server that kept saying "different business" to a phone it had just told
// "same business" must not turn into a send loop.
let parkReleasedAt = 0

function releaseParked(): void {
  if (!queue.some((q) => q.status === 'parked')) return
  if (Date.now() - parkReleasedAt < RETRY_MS) return
  parkReleasedAt = Date.now()
  queue = queue.map(unpark)
  notify()
}

// The namespace a cache key was written under, read back off the key: 'b<id>:'
// (with a worker hash inside for its workers), 'a:<hash>:' / 'n:<hash>:', a
// bare worker hash, or '' for the legacy business-1 owner keys (whose own
// keys, 'customers', 'today:<date>', 'photos:<id>' and so on, match none of
// these). Business ids are read as at most six digits so an 8-character
// worker hash of 'b' and seven digits can never pass for one.
function cacheNamespaceOf(rest: string): string {
  const m = /^(?:b\d{1,6}:(?:[0-9a-f]{8}:)?|[an]:[0-9a-f]{8}:|[0-9a-f]{8}:)/.exec(rest)
  return m ? m[0] : ''
}

// Remove every cached READ belonging to one namespace. Queued-work records
// are never touched.
// Log out (signin.ts): this person's saved copies leave the phone at once, signal or not. Their unsent changes
// stay, held, and send if they sign back in (docs/plans/offline/PLAN-v3-changes.md §2).
export function forgetLocalReads(email: string | null = signedInEmail): void {
  const namespaces = new Set(email ? knownNamespaces()[email.toLowerCase()] ?? [] : [])
  if (partition.ns !== null) namespaces.add(partition.ns)
  for (const ns of namespaces) {
    dropCacheNamespace(ns)
    // A first visit waiting for its job id is queued work too, kept for its author.
    try {
      const key = `roundly:pending-job-visits:${ns}`
      const raw = localStorage.getItem(key)
      if (raw) {
        const list = JSON.parse(raw)
        if (Array.isArray(list)) localStorage.setItem(key, JSON.stringify(list.map((p) => p.authorEmail ? p : { ...p, authorEmail: email })))
      }
    } catch { /* unreadable waiting work stays kept */ }
    try { localStorage.removeItem(`roundly:message-records:${ns}`) } catch { /* storage off */ }
    try { localStorage.removeItem(`roundly:missed-to-tell:${ns}`) } catch { /* storage off */ }
  }
  // Legacy waiting work in a shared owner namespace becomes tied to the person logging out.
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key?.startsWith('dropby-round-queue-v2:')) continue
      const m = JSON.parse(localStorage.getItem(key) ?? 'null')
      if (m && !m.authorEmail && namespaces.has(key.slice('dropby-round-queue-v2:'.length, key.lastIndexOf(':') + 1))) {
        localStorage.setItem(key, JSON.stringify({ ...m, authorEmail: email }))
      }
    }
  } catch { /* unreadable queued work stays kept */ }
}

function dropCacheNamespace(ns: string): void {
  try {
    const doomed: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (!k || !k.startsWith(CACHE_PREFIX)) continue
      if (cacheNamespaceOf(k.slice(CACHE_PREFIX.length)) === ns) doomed.push(k)
    }
    for (const k of doomed) localStorage.removeItem(k)
  } catch {
    // storage unavailable — nothing cached was loadable anyway
  }
}

// Changes on this phone that belong to OTHER accounts: every queue record in
// another namespace, plus another business's records sharing this one (a
// worker and an admin with one email). Counted only, never read out: their
// labels are not this account's to see.
export function waitingForOtherAccounts(): number {
  // Nobody signed in (or nobody to act for): every change held on this phone
  // is waiting for its account, so all of them count.
  const everyone = partition.ns === null
  let n = 0
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (!k || !k.startsWith(RECORD_PREFIX)) continue
      const rest = k.slice(RECORD_PREFIX.length)
      if (everyone || rest.slice(0, rest.lastIndexOf(':') + 1) !== partition.ns) n++
    }
  } catch {
    return 0
  }
  for (const [ns, held] of heldInMemory) if (everyone || ns !== partition.ns) n += held.entries.length
  return n + foreignHere
}

// ---- who may be asked what ----------------------------------------------------
// Routes that belong to no business: identity discovery, setting a business
// up, the admin's own screens, liveness. Everything else is a business's data
// and is never requested without a business to ask it for.
function belongsToNoBusiness(path: string): boolean {
  const p = path.split('?')[0]
  return p === '/api/me' || p === '/api/signup' || p === '/api/health' || p.startsWith('/api/admin/')
}

function mayRequest(path: string, businessId: number | null): boolean {
  return businessId !== null || belongsToNoBusiness(path)
}

// The expected-business header for a request made for `businessId`. /api/me
// never carries it: it is how the phone finds out who is signed in.
function businessHeader(path: string, businessId: number | null): Record<string, string> {
  if (businessId === null || path.split('?')[0] === '/api/me') return {}
  return { 'X-Roundly-Business': String(businessId) }
}

// The server's answers that mean "the account signed in is not the one this
// request was made for": a different business, a sign-in on no list, or a
// paused business. A change that gets one is parked, not failed, and the
// phone asks /api/me who is signed in now.
const HOLD_CODES = new Set(['business_mismatch', 'not_a_user', 'business_suspended'])

function isBusinessHold(status: number, code: string | undefined): boolean {
  return code !== undefined && HOLD_CODES.has(code) && (status === 409 || status === 403)
}

// me.ts registers loadMe's re-check here, so a park, or another tab's
// broadcast, makes this tab ask /api/me again (me.ts coalesces bursts).
let identityRecheck: (() => Promise<unknown>) | null = null

export function setIdentityRecheck(fn: () => Promise<unknown>): void {
  identityRecheck = fn
}

function requestIdentityRecheck(): void {
  if (!identityRecheck) return
  void identityRecheck().catch(() => {
    // a failed re-check changes nothing: the identity in force stands
  })
}

type QueueListener = () => void
const listeners = new Set<QueueListener>()
let queue: QueuedMutation[] = []
// Two independent storage problems, surfaced as one warning but never
// cleared by each other: WRITE failures (queue may not survive a browser
// close) and CORRUPTION (a legacy payload or record that cannot be read
// back — data this install refuses to erase and cannot recover). A
// successful routine persist must not clear a corruption warning, and a
// corrupt scan must not be re-raised as a write failure.
let writeFailed = false
let storageCorrupt = false
// What this tab last believes storage holds, entry-for-entry. notify()
// diffs the live queue against this baseline and touches ONLY the records
// that differ — the never-write-what-you-didn't-change rule that kills the
// multi-tab overwrite. Updated by this tab's own writes and by storage
// events (external truth), never ahead of storage.
let persisted = new Map<string, QueuedMutation>()
// Intents whose record WRITE failed (quota/off): they exist only in this
// tab's memory. A storage sync must treat "absent from storage" as
// "unsaved here", not as "resolved elsewhere" — dropping them would
// silently discard the user's edit.
const localOnly = new Set<string>()
// Records this tab resolved (acked/dismissed) whose REMOVAL failed: the
// ghost must not be re-imported by a sync, and the removal is retried.
const pendingRemovals = new Set<string>()
// Records in this namespace that belong to another business (counted for the
// chip, never loaded, written or removed here).
let foreignHere = 0
// Intents this tab has a fetch outstanding for right now. A namespace that
// is loaded again while one is in flight (switched away and back) must not
// take it for an abandoned send and fire it a second time alongside.
const inFlight = new Set<string>()
// Changes that exist only in this tab's memory (their record write failed:
// storage full or off), set aside per namespace when another account becomes
// active, with any record removals still owed there. They come back when
// their namespace is active again; they are never dropped, and never shown
// or sent for anyone else.
const heldInMemory = new Map<string, { entries: QueuedMutation[]; removals: string[] }>()

// True while anything unsent exists only in this tab's memory: a change
// storage refused, in the queue in force or set aside for another account.
// A reload would lose it, so me.ts does not reload over it.
export function hasUnsavedChanges(): boolean {
  if (queue.some((m) => localOnly.has(m.key))) return true
  for (const held of heldInMemory.values()) if (held.entries.length > 0) return true
  return false
}

// The page still shows screens drawn for an account that is no longer the one
// signed in: me.ts held the reload back because unsent work exists only in
// this page's memory (a photo or voice note waiting, a change storage
// refused). Nothing NEW is asked for, read back or changed from those screens
// (it would go to the account signed in now) until their account signs back
// in or the page reloads. The queue itself keeps working for the account
// signed in.
let screensStale = false

export function setScreensStale(stale: boolean): void {
  screensStale = stale
}

// ---- storage primitives -----------------------------------------------------

function readRecord(key: string): QueuedMutation | null {
  const prefix = queueRecordPrefix()
  if (prefix === null) return null
  try {
    const raw = localStorage.getItem(prefix + key)
    if (!raw) return null
    const m = JSON.parse(raw) as QueuedMutation & { parked?: unknown }
    const businessId = recordBusinessId(m)
    if (
      !(m?.key === key &&
        typeof m.path === 'string' && m.path.startsWith('/api/') &&
        (METHODS as readonly string[]).includes(m.method) &&
        (STATUSES as readonly string[]).includes(m.status) &&
        Number.isFinite(m.createdAt) && businessId !== undefined)
    ) {
      return null
    }
    // The stored form of a parked change (storedForm) reads back as parked;
    // the mark itself never rides in memory.
    const { parked, ...entry } = m
    return { ...entry, businessId, status: parked === true && entry.status === 'queued' ? 'parked' : entry.status }
  } catch {
    return null
  }
}

// What a record looks like on disk. Only statuses yesterday's app knows are
// ever written: a parked change is stored as an ordinary waiting one plus
// `"parked":true`, a field yesterday's app ignores. After a rollback it is a
// change yesterday's app can see and send (without a business header, which
// the server takes as Dropby's); today's app reads the mark back as parked.
function storedForm(m: QueuedMutation): QueuedMutation | (QueuedMutation & { parked: true }) {
  return m.status === 'parked' ? { ...m, status: 'queued', parked: true } : m
}

function writeRecord(m: QueuedMutation): boolean {
  const prefix = queueRecordPrefix()
  if (prefix === null) return false
  try {
    localStorage.setItem(prefix + m.key, JSON.stringify(storedForm(m)))
    return true
  } catch {
    return false
  }
}

function removeRecord(key: string): boolean {
  const prefix = queueRecordPrefix()
  if (prefix === null) return false
  try {
    localStorage.removeItem(prefix + key)
    return true
  } catch {
    return false
  }
}

// This namespace's records only (see isOwnRecordKey), and of those only this
// business's: another business's records sharing the namespace are counted
// and left alone.
function scanRecords(): { raw: QueuedMutation[]; corrupt: boolean; foreign: number } {
  const raw: QueuedMutation[] = []
  let corrupt = false
  let foreign = 0
  const prefix = queueRecordPrefix()
  if (prefix === null) return { raw, corrupt, foreign }
  try {
    const n = localStorage.length
    for (let i = 0; i < n; i++) {
      const k = localStorage.key(i)
      if (!k || !isOwnRecordKey(k)) continue
      const m = readRecord(k.slice(prefix.length))
      if (!m) corrupt = true // unreadable record: left in place, surfaced below
      else if (m.businessId !== queueBusiness() || (m.authorEmail && m.authorEmail !== signedInEmail)) foreign++
      else raw.push(m)
    }
  } catch {
    return { raw, corrupt: true, foreign }
  }
  return { raw, corrupt, foreign }
}

// ---- v1 → v2 migration --------------------------------------------------------

const METHODS = ['POST', 'PATCH', 'DELETE'] as const
// EVERY status a record can legitimately carry. A status missing from this
// list is read back as corruption, and the intent disappears from the queue
// on the next load — which is exactly what "Sign in again" does. 'signed-out'
// belongs here for the same reason 'conflict' does: it is a hold, not junk.
// 'parked' is only ever READ here (a record written by an earlier build of
// this change); it is never written (storedForm).
const STATUSES = ['sending', 'queued', 'conflict', 'failed', 'signed-out', 'parked'] as const

// The business a stored record belongs to. Yesterday's records carry none,
// and only business 1 existed then; null is a no-business account's change.
// Anything else is unreadable (undefined), never guessed.
function recordBusinessId(m: unknown): number | null | undefined {
  if (!m || typeof m !== 'object' || !('businessId' in m)) return 1
  const v = (m as { businessId?: unknown }).businessId
  if (v === undefined) return 1
  if (v === null) return null
  return isPositiveSafeInt(v) ? v : undefined
}

// Accept only entries shaped like a mutation; UUID/status/body ride across
// untouched. Junk entries are skipped, recoverable ones are kept.
function sanitizeEntry(v: unknown): QueuedMutation | null {
  if (!v || typeof v !== 'object') return null
  const m = v as Record<string, unknown>
  if (
    typeof m.key !== 'string' ||
    !(METHODS as readonly string[]).includes(m.method as (typeof METHODS)[number]) ||
    !(STATUSES as readonly string[]).includes(m.status as (typeof STATUSES)[number])
  ) {
    return null
  }
  return {
    key: m.key,
    label: typeof m.label === 'string' ? m.label : `${String(m.method)} ${String(m.path ?? '')}`,
    method: m.method as QueuedMutation['method'],
    path: typeof m.path === 'string' ? m.path : '/',
    body: m.body,
    status: m.status as QueuedMutation['status'],
    error: typeof m.error === 'string' ? m.error : undefined,
    createdAt: typeof m.createdAt === 'number' ? m.createdAt : Date.now(),
    claim: null,
    // v1 predates businesses: its array was business 1's owner's.
    businessId: 1,
    // v1 attempted every intent at creation — its records were only ever
    // written around a send. Frozen bodies from day one.
    attempted: true,
  }
}

function loadSeenKeys(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY)
    const list = raw ? (JSON.parse(raw) as unknown) : []
    return Array.isArray(list) ? new Set(list.filter((k): k is string => typeof k === 'string')) : new Set()
  } catch {
    return new Set()
  }
}

let seenKeys = loadSeenKeys()

function rememberKeys(keys: string[]): void {
  if (keys.length === 0) return
  for (const k of keys) seenKeys.add(k)
  if (seenKeys.size > SEEN_MAX * 2) {
    seenKeys = new Set([...seenKeys].slice(-SEEN_MAX))
  }
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...seenKeys]))
  } catch {
    // advisory only — losing it risks re-importing a resolved intent from a
    // legacy array, never losing one
  }
}

// Move the v1 whole-queue array into per-intent records. The source key is
// erased ONLY after the payload parsed AND every entry in it persisted at
// its destination AND none was malformed — a malformed entry is pending
// data this code cannot recover and must not silently erase, so the source
// stays (with the corruption warning raised) for as long as it holds
// anything unreadable. An unparsable v1 payload is the same rule. A failed
// destination write leaves v1 exactly as it was for the next load.
function migrateLegacy(): void {
  // The shared v1 array predates the partition: it belongs to whoever used
  // the phone before the upgrade — in practice business 1's owner. Anyone
  // else signing in (a worker, another business, an admin) must neither
  // adopt nor erase it.
  if (queueNamespace() !== '' || queueBusiness() !== 1) return
  let raw: string | null = null
  try {
    raw = localStorage.getItem(LEGACY_KEY)
  } catch {
    return
  }
  if (!raw) return
  let items: unknown
  try {
    items = JSON.parse(raw)
  } catch {
    storageCorrupt = true
    return
  }
  if (!Array.isArray(items)) {
    storageCorrupt = true
    return
  }
  const migrated: string[] = []
  let malformed = false
  let writeFailedHere = false
  for (const it of items) {
    const m = sanitizeEntry(it)
    if (!m) {
      malformed = true // kept in v1 — unrecoverable, not erased
      continue
    }
    if (seenKeys.has(m.key)) continue // already resolved by v2 — do not resurrect
    migrated.push(m.key)
    if (readRecord(m.key)) continue // a previous pass wrote it but died before cleanup
    if (!writeRecord(m)) {
      writeFailedHere = true
      break // destination did NOT persist — v1 stays intact, retry next load
    }
  }
  if (malformed) storageCorrupt = true
  if (writeFailedHere) writeFailed = true
  if (malformed || writeFailedHere) return
  try {
    localStorage.removeItem(LEGACY_KEY)
  } catch {
    // legacy stuck in place; seenKeys keeps the next migration idempotent
  }
  rememberKeys(migrated)
}

// ---- load / cross-tab sync ----------------------------------------------------

function claimLiveElsewhere(m: QueuedMutation): boolean {
  return (
    m.status === 'sending' &&
    m.claim !== null &&
    m.claim !== undefined &&
    m.claim.by !== TAB_ID &&
    Date.now() - m.claim.at < CLAIM_STALE_MS
  )
}

// Anything persisted mid-'sending' never acked (its tab died or lost
// signal) is retried — unless ANOTHER tab holds a fresh claim on it, in
// which case this tab leaves it entirely alone, or THIS tab is still waiting
// on its fetch (the namespace was switched away and back meanwhile).
function normalizeLoaded(m: QueuedMutation): QueuedMutation {
  if (m.status !== 'sending') return m
  if (claimLiveElsewhere(m) || inFlight.has(m.key)) return m
  return { ...m, status: 'queued' as const, claim: null }
}

function orderQueue(list: QueuedMutation[]): QueuedMutation[] {
  return [...list].sort((a, b) => a.createdAt - b.createdAt || (a.key < b.key ? -1 : 1))
}

// Adopt what storage holds RIGHT NOW without writing anything back: entries
// resolved by another tab (acked, dismissed, cancelled) vanish from memory
// instead of being resurrected by this tab's next write; entries changed or
// added elsewhere come in. Three kinds of entry are deliberately NOT taken
// from storage: this tab's own in-flight sends (the local fetch is the live
// truth), intents whose record write failed here (they are unsaved LOCAL
// work — dropping them would silently discard the user's edit), and records
// this tab resolved but could not yet remove (their ghosts must not come
// back while the removal is still owed).
function syncFromStorage(): void {
  const scan = scanRecords()
  if (scan.corrupt) storageCorrupt = true
  foreignHere = scan.foreign
  const stored = new Map(scan.raw.map((m) => [m.key, m]))
  const next: QueuedMutation[] = []
  let changed = false
  for (const m of queue) {
    const s = stored.get(m.key)
    if (localOnly.has(m.key)) {
      next.push(m)
      stored.delete(m.key)
      continue
    }
    if (!s) {
      if (localOnly.has(m.key)) {
        next.push(m) // unsaved here, unknown elsewhere — keep it, retry the write
        stored.delete(m.key)
        continue
      }
      if (scan.corrupt) {
        // The scan is incomplete (unreadable record or failed enumeration):
        // absence here proves nothing. Only a complete, clean scan may
        // conclude an intent was resolved elsewhere — otherwise keep it and
        // keep the warning.
        next.push(m)
        stored.delete(m.key)
        continue
      }
      changed = true // resolved elsewhere — dropped, never resurrected
      continue
    }
    stored.delete(m.key)
    if (m.status === 'sending' && m.claim?.by === TAB_ID) {
      next.push(m) // ours and genuinely in flight here
      continue
    }
    const merged = normalizeLoaded(s)
    if (
      merged !== s ||
      merged.status !== m.status ||
      merged.error !== m.error ||
      merged.claim !== m.claim
    ) {
      changed = true
    }
    next.push(merged)
  }
  for (const s of stored.values()) {
    if (pendingRemovals.has(s.key)) continue // resolved here; removal still owed
    changed = true // queued in another tab while this one was open
    next.push(normalizeLoaded(s))
  }
  queue = orderQueue(next)
  // storage IS the baseline now — a sync must never trigger write-backs
  persisted = new Map([
    ...(scan.corrupt ? persisted : new Map<string, QueuedMutation>()),
    ...scan.raw.map((m): [string, QueuedMutation] => [m.key, m]),
  ])
  // A removal still owed keeps its ghost in the baseline (the record is in
  // scan.raw, so it already is); if another tab removed it in the meantime,
  // nothing is owed any more.
  for (const key of pendingRemovals) {
    if (!scan.corrupt && !persisted.has(key)) pendingRemovals.delete(key)
  }
  if (changed || scan.corrupt) for (const fn of listeners) fn()
}

// Another tab changed the queue (or an old-version tab rewrote the v1
// array). Storage events don't fire in the tab that wrote — only in the
// others — which is exactly the audience here. Before the identity answers,
// nothing is loaded, so there is nothing to sync into.
window.addEventListener('storage', (e: StorageEvent) => {
  // Another tab heard a different answer to "who is signed in": ask the
  // server here too rather than trusting the record (the broadcast is a
  // convenience; the X-Roundly-Business header is the boundary).
  if (e.key === IDENTITY_KEY) {
    if (identified && e.newValue !== null && e.newValue !== ownIdentityRecord) requestIdentityRecheck()
    return
  }
  if (partition.ns === null) return
  if (e.key === LEGACY_KEY) {
    if (partition.ns !== '') return // the legacy array is only business 1's owner's
    if (e.newValue === null) return // our own migration's removal
    migrateLegacy() // an old-version tab is still writing whole-queue v1
    syncFromStorage()
    return
  }
  if (e.key === null || isOwnRecordKey(e.key)) {
    // key null = storage cleared: nothing is persisted, so nothing is owed
    syncFromStorage()
  }
})

// ---- persistence ---------------------------------------------------------------

function sameEntry(a: QueuedMutation, b: QueuedMutation): boolean {
  return (
    a.key === b.key &&
    a.status === b.status &&
    a.error === b.error &&
    a.createdAt === b.createdAt &&
    a.method === b.method &&
    a.path === b.path &&
    a.label === b.label &&
    a.attempted === b.attempted &&
    a.businessId === b.businessId &&
    a.claim?.by === b.claim?.by &&
    a.claim?.at === b.claim?.at &&
    JSON.stringify(a.body) === JSON.stringify(b.body)
  )
}

// Persist exactly what THIS tab changed: additions and edits are written
// per-record, removals remove their own record. No whole-queue write
// exists any more, so no tab can clobber another tab's intents. Failures
// are remembered per-record (localOnly / pendingRemovals) so they are
// retried, and a sync can tell them apart from external resolutions.
function persistQueueChanges(): void {
  let allOk = true
  const next = new Map<string, QueuedMutation>()
  const live = new Set<string>()
  for (const m of queue) {
    live.add(m.key)
    const prev = persisted.get(m.key)
    if (!prev || !sameEntry(prev, m)) {
      if (writeRecord(m)) {
        next.set(m.key, m)
        localOnly.delete(m.key)
      } else {
        allOk = false
        localOnly.add(m.key) // unsaved local work — a sync must keep it
      }
    } else {
      next.set(m.key, prev)
    }
  }
  for (const [key, prev] of persisted) {
    if (live.has(key)) continue
    if (removeRecord(key)) {
      localOnly.delete(key)
      pendingRemovals.delete(key)
      continue
    }
    next.set(key, prev) // keep the ghost so the removal is retried
    pendingRemovals.add(key)
    allOk = false
  }
  // A change acknowledged before its record was ever saved has left the
  // queue: nothing about it is unsaved any more.
  for (const key of localOnly) if (!live.has(key)) localOnly.delete(key)
  writeFailed = !allOk
  persisted = next
}

// False when the queue is not safely persisted — a write failed (won't
// survive a browser close) or storage holds data this install cannot read
// back. The chip must say so in both cases.
export function queuePersistenceOk(): boolean {
  return !writeFailed && !storageCorrupt
}

function notify(): void {
  persistQueueChanges()
  for (const fn of listeners) fn()
}

export function onQueueChange(fn: QueueListener): void {
  listeners.add(fn)
}

// A listener for one change's life only (Book them in waits for its booking to land): taken off once it has heard.
export function offQueueChange(fn: QueueListener): void {
  listeners.delete(fn)
}

export function getQueue(): readonly QueuedMutation[] {
  return queue
}

// ---- send / acknowledgement -----------------------------------------------------

// What a visit save did to money brought from Squeegee (0043): the cash taken, what was kept as credit, and what is
// on the visit now. Numbers only; anything else is dropped.
export interface MoneyNotice {
  receipt_pence?: number | null
  kept_as_credit_pence?: number
  from_credit_pence?: number
  to_pay_pence?: number
}
function moneyOf(o: Record<string, unknown>): MoneyNotice {
  const out: MoneyNotice = {}
  for (const k of ['kept_as_credit_pence', 'from_credit_pence', 'to_pay_pence'] as const) if (typeof o[k] === 'number') out[k] = o[k] as number
  if (typeof o.receipt_pence === 'number' || o.receipt_pence === null) out.receipt_pence = o.receipt_pence as number | null
  return out
}

// "This visit and every one after" (tidy-up 2, item 2, functions/lib/price-onward.ts): what the save changed, as the
// server read it back off the save's own History rows, and what an Undo of it put back and left. Shapes checked here;
// anything else is dropped (Astra F08, Sol R07: the answer reaches the screen on direct, queued and replayed paths).
export interface OnwardDone {
  key: string
  usual: { owner: 'customer' | 'job'; id: number; from: number | null; to: number; changed: boolean }
  visits: { id: number; date: string }[]
}
export interface UndoDone {
  key: string
  of: string
  undone: { visit: boolean; usual: boolean; visits: { id: number; date: string }[] }
  left: string[]
  words: string | null
}
const datedVisits = (v: unknown): { id: number; date: string }[] =>
  Array.isArray(v) ? v.filter((x): x is { id: number; date: string } => isObject(x) && isPositiveSafeInt(x.id) && typeof x.date === 'string').slice(0, 60).map((x) => ({ id: x.id, date: x.date })) : []
function onwardDoneOf(o: unknown): OnwardDone | null {
  if (!isObject(o) || typeof o.key !== 'string' || !isObject(o.usual)) return null
  const u = o.usual
  if (!isPositiveSafeInt(u.id) || typeof u.to !== 'number' || !(typeof u.from === 'number' || u.from === null)) return null
  return { key: o.key, usual: { owner: u.owner === 'job' ? 'job' : 'customer', id: u.id, from: u.from as number | null, to: u.to, changed: u.changed === true }, visits: datedVisits(o.visits) }
}
function undoDoneOf(o: unknown): UndoDone | null {
  if (!isObject(o) || typeof o.key !== 'string' || typeof o.of !== 'string' || !isObject(o.undone)) return null
  const d = o.undone
  return {
    key: o.key, of: o.of,
    undone: { visit: d.visit === true, usual: d.usual === true, visits: datedVisits(d.visits) },
    left: Array.isArray(o.left) ? o.left.filter((x): x is string => typeof x === 'string').slice(0, 60) : [],
    words: typeof o.words === 'string' ? o.words : null,
  }
}

export interface Ack<T = unknown> {
  ok: boolean
  status: number
  row?: T
  money?: MoneyNotice
  // The price carried on (OnwardDone) or its Undo (UndoDone), when the save was one.
  onward?: OnwardDone
  undo?: UndoDone
  // Present when logging a clean auto-created the next visit from the
  // customer's cycle — the UI says so rather than letting a booking appear
  // out of nowhere.
  nextBooked?: { date: string; source?: string | null } | null
  // True when a "create" actually put back a clean that had been removed —
  // the row carries its ORIGINAL amount, not the price the caller sent, so
  // the UI has to say so rather than let the money change quietly.
  restored?: boolean
  // Bookings the server withdrew alongside a deleted clean (app-generated
  // dates only).
  removedBookings?: { date: string }[]
  replayed?: boolean
  noop?: boolean
  // Cloudflare Access answered its own login page instead of the API. The
  // request never reached the server, so this is neither a save nor a
  // rejection — it is "sign in again", and it must stop the retry loop.
  signedOut?: boolean
  // Voice notes: the recognized text, on a successful transcription upload.
  transcript?: string
  // On a 409 the server hands back the CURRENT row (conflict contract) — the
  // caller can adopt its version and retry instead of throwing the work away.
  current?: Record<string, unknown>
  // With a 409 for a change that said what it was made on: the boxes someone else changed first.
  changed?: TheirChange[]
  error?: string
  // The server's machine-readable reason on a refusal (e.g.
  // 'business_mismatch'), when it gave one.
  code?: string
  // The change is held for its own business (status 'parked' in the queue).
  parked?: boolean
  // A bank payment that paid an account brought from Squeegee (0043): its receipt, for "Undo".
  receiptId?: number
  // Book them in (POST /api/leads/:id/book, 19 Oct 2026): the customer made and the visit booked, as the server
  // answered them (null when none was made: "Yes, this is them" links only).
  booked?: { customer: Record<string, unknown> | null; clean: Record<string, unknown> | null }
}

// Access does not answer 401 to a browser: it 302s to the team login page,
// which fetch follows, so the app sees a 200 carrying HTML. Treated as an
// ordinary unreadable answer it retried forever under "waiting for signal",
// blaming the van's connection for an expired session.
function isAccessLoginPage(res: Response): boolean {
  // Roundly's own sign-in (0025) says it plainly: a 401 marked signed-out.
  if (res.status === 401 && res.headers.get('X-Roundly-Signed-Out') === '1') return true
  if (res.redirected && /(^|\.)cloudflareaccess\.com/.test(new URL(res.url, 'http://x').hostname)) return true
  // Only a SUCCESSFUL HTML answer is the login page. A 5xx HTML error page is
  // an ordinary server failure and must stay retryable under the offline
  // contract — parking it as "sign in again" would hold a good edit until
  // someone signed in, for a problem signing in cannot fix.
  return res.ok && (res.headers.get('content-type') ?? '').includes('text/html')
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function isPositiveSafeInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isSafeInteger(v) && v > 0
}

// The several-jobs marker, for a change whose record says the screen that made it knew about several jobs, and
// for no other: never from the code doing the sending.
export function jobsHeader(m: Pick<QueuedMutation, 'jobsAware'>): Record<string, string> {
  return m.jobsAware === true ? { 'X-Roundly-Jobs': '1' } : {}
}

// The cash-amounts marker, the same way: from the record, never from the code doing the sending.
export function moneyHeader(m: Pick<QueuedMutation, 'moneyAware'>): Record<string, string> {
  return m.moneyAware === true ? { 'X-Roundly-Money': '1' } : {}
}

async function send<T>(m: QueuedMutation): Promise<Ack<T>> {
  let res: Response
  try {
    res = await fetch(m.path, {
      method: m.method,
      // The business this change was made for — never the one signed in
      // now: the server refuses it (business_mismatch) under any other.
      headers: { 'content-type': 'application/json', 'Idempotency-Key': m.key, ...businessHeader(m.path, m.businessId), ...jobsHeader(m), ...moneyHeader(m) },
      body: JSON.stringify(m.body ?? {}),
      signal: AbortSignal.timeout(SEND_TIMEOUT_MS),
    })
  } catch {
    return { ok: false, status: 0, error: 'no connection' }
  }
  if (isAccessLoginPage(res)) return { ok: false, status: res.status, signedOut: true, error: SIGNED_OUT_MESSAGE }
  let body: Record<string, unknown> | null = null
  try {
    const parsed: unknown = await res.json()
    if (isObject(parsed)) body = parsed
  } catch {
    body = null // HTML page / truncated body — not an acknowledgement
  }
  if (res.ok) {
    // A 200 is only a save when it carries a real acknowledgement row.
    // Every mutation variant answers { row, … } where row is the stored row
    // WITH its id — plain writes, soft deletes, no-op patches, replays
    // (reorder answers ONE row, no-op reorder included; day-note deletes
    // answer the deleted row). An HTML error page, an empty object, a row
    // without an id or garbage here used to be accepted as success: the
    // intent was cleared and the change LOOKED saved while nothing said so.
    // Invalid success is a retryable failure — the intent stays queued,
    // never cleared. Optional notice fields with broken shapes are dropped
    // rather than passed through: UI callbacks trust their types.
    const row = body?.row
    if (!body || !isObject(row) || !isPositiveSafeInt(row.id)) {
      return { ok: false, status: res.status, error: 'unreadable confirmation from the server — kept for retry' }
    }
    const rawNext = body.next_booked
    const nextBooked =
      isObject(rawNext) && typeof rawNext.date === 'string'
        ? { date: rawNext.date, source: typeof rawNext.source === 'string' ? rawNext.source : null }
        : null
    // Money brought from Squeegee (0043): what this save did to it, when anything ("£5 kept as credit").
    const rawMoney = body.money
    const moneyNotice = isObject(rawMoney) ? { money: moneyOf(rawMoney) } : {}
    const rawRemoved = body.removed_bookings
    const removedBookings = Array.isArray(rawRemoved)
      ? rawRemoved.filter((d): d is { date: string } => isObject(d) && typeof d.date === 'string')
      : []
    return {
      ok: true,
      status: res.status,
      row: row as T,
      nextBooked,
      restored: body.restored === true,
      removedBookings,
      replayed: body.replayed === true,
      noop: body.noop === true,
      ...moneyNotice,
      ...(onwardDoneOf(body.onward) ? { onward: onwardDoneOf(body.onward) as OnwardDone } : {}),
      ...(undoDoneOf(body.undo) ? { undo: undoDoneOf(body.undo) as UndoDone } : {}),
      ...(typeof body.receipt_id === 'number' ? { receiptId: body.receipt_id } : {}),
      ...('clean' in body && 'customer' in body
        ? { booked: { customer: isObject(body.customer) ? body.customer : null, clean: isObject(body.clean) ? body.clean : null } }
        : {}),
    }
  }
  const error = typeof body?.error === 'string' ? body.error : `HTTP ${res.status}`
  const code = typeof body?.code === 'string' ? body.code : undefined
  // A 409 carries the row as it is now (the conflict contract, Ack.current).
  // It was read on uploads only, so an ordinary save's caller could never
  // take the fresh version and try again (worker review 2026-09-24).
  const current = res.status === 409 && isObject(body?.current) ? { current: body.current as Record<string, unknown> } : {}
  const changed = res.status === 409 ? theirChangesOf(body?.changed) : []
  return { ok: false, status: res.status, error, ...(code ? { code } : {}), ...current, ...(changed.length ? { changed } : {}) }
}

// The server's list of boxes someone else changed, kept only where each entry has the right shape.
function theirChangesOf(v: unknown): TheirChange[] {
  if (!Array.isArray(v)) return []
  return v
    .filter((c): c is Record<string, unknown> => isObject(c) && typeof c.field === 'string')
    .slice(0, 20)
    .map((c) => ({
      field: c.field as string,
      value: c.value === null || ['string', 'number', 'boolean'].includes(typeof c.value) ? c.value : null,
      by: typeof c.by === 'string' ? c.by : null,
    }))
}

// Where the browser offers Web Locks, two tabs never send the same intent
// at the same moment even if both flush simultaneously. Returns null when
// another tab holds the lock — the intent stays queued and that tab's
// outcome arrives via its record + storage event.
interface LockManagerLike {
  request(name: string, opts: { ifAvailable: boolean }, fn: (lock: unknown) => unknown): Promise<unknown>
}

async function sendCoordinated<T>(m: QueuedMutation): Promise<Ack<T> | null> {
  const locks = typeof navigator !== 'undefined' ? (navigator as { locks?: LockManagerLike }).locks : undefined
  if (!locks?.request) return send<T>(m)
  try {
    const out = await locks.request(`dropby-send:${m.key}`, { ifAvailable: true }, async (lock) =>
      lock ? await send<T>(m) : null,
    )
    return (out as Ack<T> | null) ?? null
  } catch {
    return send<T>(m) // locks themselves failed — the claim record still guards
  }
}

// After a mutation acks, still-queued mutations to the SAME row (same
// method+path) would carry a stale version and 409 against the user's OWN
// edit (kimi LB-2 — the normal two-cells-in-a-row gesture). Chain the fresh
// version forward — but ONLY into intents that have NEVER been attempted.
// An attempted intent's body is frozen under its idempotency key: the
// server binds the key to a request hash, and a retry with an edited body
// is a different request wearing a committed key (guaranteed 409, and a
// lie about what was sent). An attempted-but-unacknowledged intent retries
// byte-identical; if its stale version then conflicts, that is surfaced as
// the conflict it is, for the user to reload and re-make the edit — never
// silently rewritten, never auto-rekeyed (a fresh key would blindly repeat
// a mutation that may already have committed).
function chainVersion(from: QueuedMutation, row: unknown): void {
  const v = (row as { version?: unknown } | undefined)?.version
  if (typeof v !== 'number') return
  queue = queue.map((q) => {
    if (q.key === from.key || q.status !== 'queued') return q
    if (q.attempted !== false) return q // only never-attempted bodies may change
    if (q.method !== from.method || q.path !== from.path) return q
    // A lock screen tap is chained only by the change it was made on (C1): one it never saw keeps its own version and
    // the server's check.
    if (q.cardAfter !== undefined && q.cardAfter !== from.key) return q
    if (!q.body || typeof q.body !== 'object' || !('version' in q.body)) return q
    const body = q.body as Record<string, unknown>
    // A box the owner's own earlier change wrote was made, in this change, on the value before it: it now stands on
    // what that change saved (same-time saves), so landing on top of the owner's own change is never read as someone
    // else's.
    const base = isObject(body.base) ? { ...body.base } : null
    const wrote = from.body && typeof from.body === 'object' ? (from.body as Record<string, unknown>) : {}
    if (base) for (const k of Object.keys(base)) if (k in wrote && isObject(row) && k in row) base[k] = (row as Record<string, unknown>)[k] ?? null
    // The earlier change landed on top of someone else's (it merged: the version moved by more than its own one), so
    // the record now holds a change this one never saw. It keeps its own version and goes through the server's own
    // checks: a box nobody else touched still merges, a strict one (a price, Done, Paid) is refused as before, never
    // waved through on the version the merge produced (Codex, 15 Oct 2026).
    const sent = (wrote as { version?: unknown }).version
    const mergedOver = typeof sent === 'number' && v !== sent + 1
    return { ...q, body: { ...body, ...(mergedOver ? {} : { version: v }), ...(base ? { base } : {}) } }
  })
}

function updateEntry(key: string, patch: Partial<QueuedMutation>): void {
  queue = queue.map((q) => (q.key === key ? { ...q, ...patch } : q))
}

function dropEntry(key: string): void {
  queue = queue.filter((q) => q.key !== key)
}

// The server said the account signed in is not the business this change was
// made for. Kept, not sent, not a conflict; the phone asks who is signed in.
function parkEntry(key: string): void {
  updateEntry(key, { status: 'parked', error: WAITING_FOR_ACCOUNT, claim: null })
  notify()
  requestIdentityRecheck()
}

// A send answered after the phone switched accounts: the answer is dropped
// unread. Only if its change is in the queue in force (the phone switched
// away and back while it was on its way) is this tab's own claim on it let
// go, so the next pass sends it again under its own key and the server
// replays it. Nothing of the answer is written anywhere.
function releaseOwnClaim(key: string): void {
  const q = queue.find((e) => e.key === key)
  if (!q || q.status !== 'sending' || q.claim?.by !== TAB_ID) return
  updateEntry(key, { status: 'queued', claim: null })
  notify()
  void flushQueue()
}

export interface MutateOptions {
  label: string
  method: 'POST' | 'PATCH' | 'DELETE'
  path: string
  body: unknown
  onAck?: (ack: Ack) => void
  onConflict?: (ack: Ack) => void
  onQueued?: (m: QueuedMutation) => void
  // The screen making this change understands several jobs (see QueuedMutation.jobsAware). Left out, it is this
  // app's own answer (setJobsAware): every screen of this version knows a customer's jobs.
  jobsAware?: boolean
}

// Several jobs (0042, step 2): this version's screens all know about a customer's several jobs, so on a round that
// has them switched on every change is MADE marked (saved on its queued record, PLAN 2c). Set from /api/me (me.ts).
// Off, nothing is marked: a round that is off has no customer with more than one job, so the server never asks.
let jobsAwareDefault = false
export function setJobsAware(on: boolean): void {
  jobsAwareDefault = on
}

// Money brought from Squeegee (0043): set from /api/me (me.ts). Off, nothing is marked: a round without brought money
// never reads the marker, and its queued records stay exactly as before.
let moneyAwareDefault = false
export function setMoneyAware(on: boolean): void {
  moneyAwareDefault = on
}

// Run a mutation now. On network/5xx failure it parks in the retry queue
// and resolves via a later background retry (the queue chip stays honest).
//
// SEQUENTIAL SAME-ROW EDITS: if an earlier intent to the same method+path
// is still unresolved (sending, queued, or parked for review), the new
// intent is enqueued as NEVER-ATTEMPTED and not sent yet. Sending it now
// would race its own stale version against an edit that hasn't resolved —
// and rewriting the earlier body under its used key is forbidden by the
// request-hash contract. Instead the queue keeps row order: the earlier
// intent resolves first, its ack chains the fresh version into this
// never-attempted follower (legal — the key has never been sent, so no
// server hash exists to contradict), and only then does the follower send.
// A same-row conflict or failed entry HOLDS its followers for review rather
// than letting them overwrite newer data; unrelated rows are never blocked.
export async function mutate<T = unknown>(opts: MutateOptions): Promise<Ack<T>> {
  // From screens drawn for an account that is no longer signed in: not made
  // at all (it would go to the account signed in now). A refusal, so the
  // screen says so; nothing is queued.
  if (screensStale) return { ok: false, status: 403, code: 'business_mismatch', error: ACCOUNT_CHANGED_MESSAGE }
  // Once /api/me has answered, a change is only ever made for the business
  // signed in. With none to make it for (a paused business, or an account
  // with no business asking for business data) it is refused on the phone:
  // never sent, and never held for whoever signs in next.
  if (identified && (queueNamespace() === null || !mayRequest(opts.path, partition.businessId))) {
    return { ok: false, status: 403, error: NO_BUSINESS_MESSAGE }
  }
  const hasEarlier = queue.some((q) => q.method === opts.method && q.path === opts.path)
  // Restored with no signal: recorded in the queue (and saved on the phone), sent once the server confirms who
  // this is. Held exactly like a change made before the first answer.
  const heldLocal = localOnlyMode
  // No identity yet (screens can't normally run before /api/me answers, but
  // the send must be impossible, not merely unlikely): park exactly like a
  // behind-earlier intent — the partition's first flush releases it.
  const hold = hasEarlier || !identified || heldLocal
  const m: QueuedMutation = {
    key: crypto.randomUUID(),
    label: opts.label,
    method: opts.method,
    path: opts.path,
    body: opts.body,
    status: hold ? 'queued' : 'sending',
    error: hold && hasEarlier ? BEHIND_EARLIER : undefined,
    // Preserve causal order even when two edits occur in the same millisecond.
    createdAt: queue.reduce((latest, q) => Math.max(latest, q.createdAt + 1), Date.now()),
    claim: hold ? null : { by: TAB_ID, at: Date.now() },
    businessId: queueBusiness(),
    ...(signedInEmail ? { authorEmail: signedInEmail } : {}),
    // No earlier same-row work: the first fetch starts immediately below,
    // so the body is frozen from the moment the intent exists. Behind one:
    // never-attempted until the earlier intent resolves and the fresh
    // version is chained in.
    attempted: !hold,
    ...((opts.jobsAware ?? jobsAwareDefault) === true ? { jobsAware: true } : {}),
    // On a round with brought money, every change this build makes says it records cash amounts (moneyAware).
    ...(moneyAwareDefault ? { moneyAware: true } : {}),
  }
  draftEvent('roundly:save-started', m.key)
  rememberKeys([m.key])
  queue = [...queue, m]
  notify()
  if (hold) {
    if (!localOnly.has(m.key)) draftEvent('roundly:save-stored', m.key)
    opts.onQueued?.(m)
    return { ok: false, status: 0, error: m.error }
  }

  const gen = partition.gen
  inFlight.add(m.key)
  let ack: Ack<T>
  try {
    ack = await send<T>(m)
  } finally {
    inFlight.delete(m.key)
  }
  // The phone switched accounts while this was on its way: the answer is the
  // previous account's and nothing of it is applied (releaseOwnClaim).
  if (gen !== partition.gen) {
    releaseOwnClaim(m.key)
    return { ok: false, status: 0, error: WAITING_FOR_ACCOUNT }
  }
  if (ack.ok) {
    draftEvent('roundly:save-stored', m.key)
    cleanLeft(m, 'landed', ack.row)
    dropEntry(m.key)
    chainVersion(m, ack.row)
    notify()
    // Light nav refresh only; onAck owns the screen's redraw for a direct save.
    window.dispatchEvent(new CustomEvent('dropby:saved'))
    opts.onAck?.(ack)
    return ack
  }
  if (isBusinessHold(ack.status, ack.code)) {
    // Kept for its own business, like any other park: the caller is told it
    // is held (status 0, as for a change waiting for signal), not that it
    // conflicted or was refused.
    parkEntry(m.key)
    opts.onQueued?.(queue.find((q) => q.key === m.key) ?? m)
    return { ok: false, status: 0, error: WAITING_FOR_ACCOUNT, code: ack.code, parked: true }
  }
  if (ack.status === 409) {
    rowSeen(m.path, ack.current)
    updateEntry(m.key, { status: 'conflict', error: ack.error, claim: null, ...(ack.changed?.length ? { theirs: ack.changed } : {}) })
    notify()
    opts.onConflict?.(ack)
    return ack
  }
  if (ack.signedOut) {
    // The Access session expired mid-edit. Held, not failed: nothing is wrong
    // with the edit, and it sends itself once a read proves the session is
    // back — no re-typing, no retry storm against a login page. The caller is
    // told, like any other park: the customer editor releases its edit lock
    // in onQueued, and without this the field stayed frozen mid-save.
    signedOutAt = Date.now()
    updateEntry(m.key, { status: 'signed-out', error: SIGNED_OUT_MESSAGE, claim: null })
    notify()
    opts.onQueued?.(queue.find((q) => q.key === m.key) ?? m)
    return ack
  }
  if (ack.status === 401 || ack.status === 403) {
    // Signed out / not allowed. NOT dropped (a vanished entry reads as
    // saved) and not timer-retried (a 401 needs a login, not another send)
    // — parked failed, red and explicit, like every other rejection.
    updateEntry(m.key, { status: 'failed', error: ack.error, claim: null })
    notify()
    return ack
  }
  if (ack.status >= 400 && ack.status < 500) {
    // Invalid edit (400 validation / 422 Enfield) — not queueable.
    dropEntry(m.key)
    notify()
    return ack
  }
  // Offline, server error or an unreadable 200 — keep visible, retry in
  // background.
  updateEntry(m.key, { status: 'queued', error: ack.error, claim: null })
  notify()
  if (!localOnly.has(m.key)) draftEvent('roundly:save-stored', m.key)
  opts.onQueued?.(queue.find((q) => q.key === m.key) ?? m)
  return ack
}

// ---- uploads (voice notes) ---------------------------------------------------
//
// A multipart mutation that deliberately does NOT use the retry queue: the
// body is a Blob, which localStorage cannot hold, and an IndexedDB blob
// store would be a second queue to keep in step with this one. Instead the
// RECORDING stays in the caller's memory and the caller offers Retry or
// Discard; Retry re-sends byte-identical bytes under the SAME key, which the
// server's request-hash replay makes safe. Everything else matches mutate():
// no optimistic UI, one Idempotency-Key per intent, hard timeout, the
// Access-login-page disguise, and a 200 that only counts as saved when it
// carries a real row.
export interface UploadOptions {
  path: string
  // Idempotency-Key — one per recording, kept across retries.
  key: string
  fields: Record<string, string>
  file: Blob
  filename: string
  // The multipart field the file rides under and the ack field that carries
  // the saved row. Voice notes send `audio` and read `row` (the defaults);
  // photos (step 2) send `image` and read `media`.
  fileField?: string
  ackField?: string
}

export async function mutateUpload<T = unknown>(opts: UploadOptions): Promise<Ack<T>> {
  // From screens drawn for an account that is no longer signed in: not sent
  // (it would go to the account signed in now). The bytes stay with the
  // caller, which keeps them for when that account is back.
  if (screensStale) return { ok: false, status: 0, code: 'business_mismatch', error: ACCOUNT_CHANGED_MESSAGE }
  // Made for the business signed in now; with none, nothing is sent (the
  // recording stays with the caller, which offers Retry).
  const businessId = partition.businessId
  if (partition.ns === null || !mayRequest(opts.path, businessId)) {
    return { ok: false, status: 0, error: NO_BUSINESS_MESSAGE }
  }
  if (localOnlyMode) return { ok: false, status: 0, error: NO_SIGNAL } // kept on the phone; sends once confirmed
  const gen = partition.gen
  const form = new FormData()
  for (const [k, v] of Object.entries(opts.fields)) form.append(k, v)
  form.append(opts.fileField ?? 'audio', opts.file, opts.filename)
  let res: Response
  try {
    res = await fetch(opts.path, {
      method: 'POST',
      headers: { 'Idempotency-Key': opts.key, ...businessHeader(opts.path, businessId) },
      body: form,
      signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
    })
  } catch {
    return { ok: false, status: 0, error: 'no connection' }
  }
  if (isAccessLoginPage(res)) return { ok: false, status: res.status, signedOut: true, error: SIGNED_OUT_MESSAGE }
  let body: Record<string, unknown> | null = null
  try {
    const parsed: unknown = await res.json()
    if (isObject(parsed)) body = parsed
  } catch {
    body = null // HTML page / truncated body — not an acknowledgement
  }
  // Answered after the phone switched accounts: the previous account's
  // answer, dropped unread. The recording is still the caller's to retry.
  if (gen !== partition.gen) return { ok: false, status: 0, error: WAITING_FOR_ACCOUNT }
  if (res.ok) {
    const row = body?.[opts.ackField ?? 'row']
    if (!body || !isObject(row) || !isPositiveSafeInt(row.id)) {
      return { ok: false, status: res.status, error: 'unreadable confirmation from the server — try again' }
    }
    return {
      ok: true,
      status: res.status,
      row: row as T,
      transcript: typeof body.transcript === 'string' ? body.transcript : undefined,
    }
  }
  const error = typeof body?.error === 'string' ? body.error : `HTTP ${res.status}`
  const code = typeof body?.code === 'string' ? body.code : undefined
  if (isBusinessHold(res.status, code)) requestIdentityRecheck()
  return {
    ok: false,
    status: res.status,
    error,
    ...(code ? { code } : {}),
    current: isObject(body?.current) ? (body.current as Record<string, unknown>) : undefined,
  }
}

// ---- background retry pass -------------------------------------------------

// One pass at a time per tab: the interval, the 'online' listener and the
// boot call used to be able to overlap, and two concurrent passes could
// both pick the same queued intent and send it twice in parallel.
let flushInFlight = false
let flushAgain = false

export async function flushQueue(): Promise<void> {
  if (partition.ns === null) return // no identity (or nobody to act for) — nothing may even be LOADED, let alone sent
  if (localOnlyMode) return // restored with no signal: nothing sends until the server confirms who this is
  if (flushInFlight) {
    flushAgain = true
    return
  }
  flushInFlight = true
  try {
    do {
      flushAgain = false
      await flushPass()
    } while (flushAgain)
  } finally {
    flushInFlight = false
  }
}

// The loop re-reads the LIVE queue each pass, never a snapshot: an ack
// chains versions into still-queued same-row edits by REPLACING their
// queue entries (chainVersion), and a snapshotted object would send the
// stale pre-chain body (caught by the offline two-edit test: cycle landed,
// price 409'd on the version the chain had already fixed).
async function flushPass(): Promise<void> {
  const seen = new Set<string>() // offline-stuck entries are skipped for THIS pass, not spun on
  const gen = partition.gen
  for (;;) {
    // Take back 'sending' entries whose claim can no longer be live (the
    // claiming tab's fetch hard-times-out at SEND_TIMEOUT_MS). Own claims
    // are skipped — those sends are genuinely in flight in this tab.
    const revived = queue.map((q) =>
      q.status === 'sending' && q.claim?.by !== TAB_ID && !claimLiveElsewhere(q)
        ? { ...q, status: 'queued' as const, claim: null }
        : q,
    )
    if (revived.some((q, i) => q !== queue[i])) {
      queue = orderQueue(revived)
      notify()
    }
    // Same-row intents flush strictly in order: a later edit to a row is
    // only sendable once every earlier intent to that method+path has left
    // the queue (acked) or been resolved by its owner (conflict/failed
    // dismissed) — never while one is in flight, still queued, or parked
    // for review. Unrelated rows are not blocked by anyone.
    let m: QueuedMutation | undefined
    for (let i = 0; i < queue.length; i++) {
      const cand = queue[i]
      if (cand.status !== 'queued' || seen.has(cand.key)) continue
      // Only ever the business signed in now (the queue in force holds no
      // other; this keeps it impossible rather than merely absent).
      if (cand.businessId !== partition.businessId || !mayRequest(cand.path, cand.businessId)) continue
      if (queue.some((q, j) => j < i && q.method === cand.method && q.path === cand.path)) continue
      m = cand
      break
    }
    if (!m) break
    seen.add(m.key)
    updateEntry(m.key, { status: 'sending', claim: { by: TAB_ID, at: Date.now() }, attempted: true })
    notify()
    inFlight.add(m.key)
    let ack: Ack | null
    try {
      ack = await sendCoordinated(m)
    } finally {
      inFlight.delete(m.key)
    }
    // The phone switched accounts while this was on its way: the answer is
    // the previous account's and nothing of it is applied (releaseOwnClaim).
    // The next pass, if any, runs for the account signed in now.
    if (gen !== partition.gen) {
      releaseOwnClaim(m.key)
      return
    }
    if (!ack) {
      // another tab is mid-send of this intent — leave it queued; that
      // tab's outcome reaches us through its record
      updateEntry(m.key, { status: 'queued', claim: null })
      notify()
      continue
    }
    if (ack.ok) {
      cleanLeft(m, 'landed', ack.row)
      dropEntry(m.key)
      chainVersion(m, ack.row)
      notify()
      // A change that waited in the queue has landed, with what the server answered (several jobs: a job made with
      // no signal books its first visit now, src/pending-jobs.ts). With what the save did with money brought from Squeegee,
      // for a Not paid yet that landed later (GPT-6.1's review of the build, E01, 20 Oct 2026: src/screens/owed.ts).
      // A price carried on, or its Undo, with what it changed and left (tidy-up 2 item 2: src/price-edit.ts).
      window.dispatchEvent(new CustomEvent('roundly:queued-landed', { detail: { key: m.key, method: m.method, path: m.path, row: ack.row, money: ack.money ?? null, onward: ack.onward ?? null, undo: ack.undo ?? null } }))
      // Your business saved from the queue (no signal at the time): the rest of the app reads these words
      // from memory, so swap them in now, or a send button would keep going the old way (Astra, 30 Sep).
      // business-settings.ts listens for this.
      if (m.method === 'PATCH' && m.path === '/api/business') window.dispatchEvent(new CustomEvent('dropby:business-saved', { detail: ack.row }))
      // A mutation that lands from the QUEUE has no onAck — the screen that
      // fired it may be long gone. Anything the server decided on its own still
      // has to reach the user, and for a van that reconnects later this is the
      // normal path, not the edge: a reinstated clean keeps its own money
      // rather than the price that was typed, and a logged clean books the next
      // visit. Both were silent here.
      // A REPLAY is the same silence in a different disguise: the server
      // recognises the idempotency key, answers "already done" and returns no
      // next_booked / restored flags, so a clean that logged itself on the
      // first (unacknowledged) attempt — and the visit it auto-booked — landed
      // with nothing on screen to say so. And a delete that withdrew booked
      // visits said nothing about them from the queue path at all.
      const replayedClean = ack.replayed === true && /\/cleans(\/|$)/.test(m.path)
      const removed = ack.removedBookings ?? []
      if (ack.restored || ack.nextBooked || replayedClean || removed.length || ack.money?.kept_as_credit_pence || ack.money?.receipt_pence === null) {
        window.dispatchEvent(
          new CustomEvent('dropby:mutation-notice', {
            detail: {
              restored: ack.restored === true,
              nextBooked: ack.nextBooked ?? null,
              replayedClean,
              removedBookings: removed,
              row: ack.row,
              money: ack.money ?? null,
            },
          }),
        )
      }
      window.dispatchEvent(new CustomEvent('dropby:data-changed'))
    } else if (ack.signedOut) {
      signedOutAt = Date.now()
      updateEntry(m.key, { status: 'signed-out', error: SIGNED_OUT_MESSAGE, claim: null })
      notify()
    } else if (isBusinessHold(ack.status, ack.code)) {
      // Signed in as another business (or none, or a paused one): held for
      // its own business, not a conflict and not a rejection.
      parkEntry(m.key)
    } else if (ack.status === 409) {
      rowSeen(m.path, ack.current)
      updateEntry(m.key, { status: 'conflict', error: ack.error, claim: null, ...(ack.changed?.length ? { theirs: ack.changed } : {}) })
      notify()
      // The screens redraw, so a change made with no signal that clashed shows as "didn't send" straight away
      // instead of still looking done (Astra, 1 Oct; same as the failed branch below).
      window.dispatchEvent(new CustomEvent('dropby:data-changed'))
    } else if (ack.status >= 400 && ack.status < 500) {
      // Server now rejects it (state moved on — e.g. a queued Confirm for a
      // clean the other owner cancelled), or the session is gone (401/403).
      // NOT droppable: an entry that vanishes reads as "it went through".
      // Park as failed — red, with the server's reason, dismissed only by
      // explicit tap.
      updateEntry(m.key, { status: 'failed', error: ack.error, claim: null })
      notify()
      window.dispatchEvent(new CustomEvent('dropby:data-changed'))
    } else {
      // Offline / 5xx / unreadable 200 — back to queued with the reason,
      // claim released. Without this the entry would sit as an OWN-claim
      // 'sending' forever: the pass above only retries 'queued', and the
      // stale-claim takeover only fires on FOREIGN claims (the live-session
      // twin of the tab-death path normalizeLoaded handles on load).
      updateEntry(m.key, { status: 'queued', error: ack.error, claim: null })
      notify()
    }
  }
}

// Dismiss a parked conflict or failed entry (the user has seen it and
// reloaded the data). Never removes queued/sending work, and never a change
// held for its business ('parked'): that one is not the user's to throw away
// as a conflict, it sends itself when its account signs in.
// Take back a change that has never been sent (several jobs: an Undo made while the change still waits for
// signal). Only one never attempted: once sent, the server may already hold it, and the caller must reverse it once
// it lands instead. True when it was taken back.
export function withdraw(key: string): boolean {
  const m = queue.find((q) => q.key === key)
  if (!m || m.attempted !== false || m.status === 'sending') return false
  cleanLeft(m, 'gone')
  queue = queue.filter((q) => q.key !== key)
  notify()
  return true
}

// The namespace of the queue in force (whose changes these are), for anything kept beside the queue that must be
// split the same way (several jobs: pending first visits, src/pending-jobs.ts). Null: nobody to keep anything for.
export function queueAuthorEmail(): string | null { return signedInEmail }

export function queuePartition(): string | null {
  return queueNamespace()
}

export function dismiss(key: string): void {
  const m = queue.find((q) => q.key === key && (q.status === 'conflict' || q.status === 'failed'))
  if (m) cleanLeft(m, 'gone')
  queue = queue.filter((q) => q.key !== key || (q.status !== 'conflict' && q.status !== 'failed'))
  notify()
}

// Stop retrying a QUEUED mutation. The request may already have reached
// the server before the acknowledgement was lost — removing it here cannot
// undo that, which is why the chip asks for confirmation with exactly that
// warning. Sending/conflict/failed entries are NOT cancellable here (they
// need ack or dismissal, not amnesia).
export function cancelQueued(key: string): void {
  const m = queue.find((q) => q.key === key && q.status === 'queued')
  if (m) cleanLeft(m, 'gone')
  queue = queue.filter((q) => q.key !== key || q.status !== 'queued')
  notify()
}

// ---- the lock screen card's taps (docs/plans/lock-screen/REAL-CARD.md, rules 3 and 3a) ----------------------------
// The iPhone app writes a card tap down and never sends it; the page takes it into this queue, which sends it like a
// Today change made with no signal. Two saved records, per account (the queue's namespace), make that safe:
//   - how every change to a job (PATCH /api/cleans/<id>) left the queue: landed (the version sent and the one the
//     server returned) or let go / taken back ('gone'). A card tap that follows one of them takes its version from it.
//   - which card taps were taken (`card`), and whether the app has been told (`told`). A tap with a record is never
//     taken again, and stays recorded until the app has been told.
// Kept 14 days (C3), except a card tap the app has not been told about.
const JOB_PATH = /^\/api\/cleans\/(\d+)$/
const OUTCOMES_PREFIX = 'roundly:job-outcomes:v1:'
const OUTCOME_DAYS = 14
const OUTCOMES_MAX = 3000
export interface JobOutcome { left?: 'landed' | 'gone'; sent?: number; returned?: number; card?: true; told?: boolean; at: number }

function outcomesKey(): string | null {
  const ns = queueNamespace()
  return ns === null || !identified || partition.businessId === null ? null : `${OUTCOMES_PREFIX}${ns}b${partition.businessId}:${signedInEmail ?? ''}`
}

export function jobOutcomes(): Record<string, JobOutcome> {
  const k = outcomesKey()
  if (!k) return {}
  let saved: Record<string, JobOutcome> = {}
  try {
    const v: unknown = JSON.parse(localStorage.getItem(k) ?? '{}')
    if (v && typeof v === 'object' && !Array.isArray(v)) saved = v as Record<string, JobOutcome>
  } catch { /* unreadable: what this page holds still counts */ }
  return { ...saved, ...unsavedOutcomes }
}

function saveOutcomes(all: Record<string, JobOutcome>): boolean {
  const k = outcomesKey()
  if (!k) return false
  const cutoff = Date.now() - OUTCOME_DAYS * 86_400_000
  const kept = Object.entries(all).filter(([, o]) => (o.card && o.told === false) || o.at >= cutoff)
  kept.sort((a, b) => a[1].at - b[1].at)
  const trimmed = kept.length > OUTCOMES_MAX ? [...kept.filter(([, o]) => o.card && o.told === false), ...kept.filter(([, o]) => !(o.card && o.told === false)).slice(-OUTCOMES_MAX)] : kept
  try {
    localStorage.setItem(k, JSON.stringify(Object.fromEntries(trimmed)))
    return true
  } catch { return false }
}

// A change to a job leaves the queue (C2: recorded before its record is removed). Its answer's row goes out first as
// `roundly:row-saved` (C5), so anything drawn from the job's last read takes it before the queue says it is gone.
// Only on a phone with the card (lock-card.ts says so): nobody else needs the records (Sonnet's review, 21 Oct 2026).
// Known at start-up: the iPhone app puts its bridge in before the page runs, so changes that land as the queue first
// flushes are recorded too (Astra's final review).
let cardInUse = typeof window !== 'undefined' && 'RoundlyIOS' in window
export function setCardInUse(on: boolean): void { cardInUse = on }
// Outcomes this page could not save (storage full): kept for this page's life so a card tap behind them still chains
// (Astra's final review). After a reload, a tap behind one it cannot find is refused for review, never guessed.
const unsavedOutcomes: Record<string, JobOutcome> = {}

function cleanLeft(m: QueuedMutation, left: 'landed' | 'gone', row?: unknown): void {
  if (m.method !== 'PATCH' || !JOB_PATH.test(m.path)) return
  if (left === 'landed') rowSeen(m.path, row, m.body && typeof m.body === 'object' ? Object.keys(m.body as Record<string, unknown>) : [])
  // A card tap waiting behind a change that was let go or taken back is refused too (Sonnet's review: it would otherwise
  // go out on a version it was never made on).
  if (left === 'gone') refuseCardFollowers(m.key)
  if (!cardInUse && m.cardAfter === undefined) return
  const all = jobOutcomes()
  const sent = (m.body as { version?: unknown } | null)?.version
  const returned = (row as { version?: unknown } | undefined)?.version
  all[m.key] = {
    ...all[m.key],
    left,
    ...(typeof sent === 'number' ? { sent } : {}),
    ...(typeof returned === 'number' ? { returned } : {}),
    at: Date.now(),
  }
  if (!saveOutcomes(all)) unsavedOutcomes[m.key] = all[m.key]
}

// Saved and told through the caller's own notify().
function refuseCardFollowers(key: string): void {
  queue = queue.map((q) => q.cardAfter === key && q.status === 'queued' && q.attempted === false
    ? { ...q, status: 'failed' as const, error: "The change before it didn't send", claim: null }
    : q)
}

// `sent`: the boxes the change itself sent, so a listener can tell a change of whose job it is from a save that only
// carries the name back (Astra's final review, round 3).
function rowSeen(path: string, row: unknown, sent: string[] = []): void {
  if (!JOB_PATH.test(path) || !isObject(row) || typeof row.version !== 'number') return
  window.dispatchEvent(new CustomEvent('roundly:row-saved', { detail: { path, row, sent } }))
}

export interface CardTap {
  key: string
  email: string
  businessId: number
  visitId: number
  field: 'started_at' | 'finished_at'
  value: string | null
  // How the time was got (0083): 'tap' for the card's own buttons and a nudge's "Starting now", 'noticed' for a nudge's
  // "Started then" / "Finished then". Missing from an app older than this: the time then has none, as before.
  how?: 'tap' | 'noticed'
  // The job's version in the card's picture, and the change that picture rested on (C1).
  base: number
  after: string | null
  at: number
  label: string
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const ISO_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/
const safeId = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v > 0

// What is wrong with a tap's own words, or null. Only what Today itself sends: a start time, a finish time, or the
// finish taken off.
function cardTapProblem(t: CardTap): string | null {
  if (typeof t.key !== 'string' || !UUID.test(t.key)) return 'key'
  if (!safeId(t.visitId)) return 'visit'
  if (!safeId(t.base)) return 'version'
  if (typeof t.at !== 'number' || !Number.isFinite(t.at)) return 'time'
  if (t.after !== null && (typeof t.after !== 'string' || t.after.length > 80)) return 'after'
  if (t.how !== undefined && t.how !== null && t.how !== 'tap' && t.how !== 'noticed') return 'how'
  if (t.field === 'started_at') return typeof t.value === 'string' && ISO_TIME.test(t.value) && Number.isFinite(Date.parse(t.value)) ? null : 'start'
  if (t.field === 'finished_at') return t.value === null || (typeof t.value === 'string' && ISO_TIME.test(t.value) && Number.isFinite(Date.parse(t.value))) ? null : 'finish'
  return 'field'
}

export interface CardIntake {
  // In the queue (now or before) or recorded: the app may forget them.
  taken: string[]
  // Too broken to be a change to a job: the app drops them.
  rejected: string[]
}

// The lock screen's taps for the account signed in here, in the app's order (B4, B7). Nothing is taken unless the queue
// is this account's, the screens are current and storage can hold it; the whole batch is added in one go and kept only
// if every record saved. Never mutate(): the key is the tap's own and nothing is sent from here.
export function adoptQueued(taps: CardTap[]): CardIntake {
  const none: CardIntake = { taken: [], rejected: [] }
  if (!identified || screensStale || partition.ns === null || partition.businessId === null || !signedInEmail) return none
  if (!mayRequest('/api/cleans/1', partition.businessId)) return none
  // What other tabs did since this one last looked.
  syncFromStorage()
  if (!queuePersistenceOk()) return none
  const outcomes = jobOutcomes()
  const out: CardIntake = { taken: [], rejected: [] }
  const batch: QueuedMutation[] = []
  const inBatch = new Set<string>()
  let createdAt = queue.reduce((latest, q) => Math.max(latest, q.createdAt + 1), Date.now())
  for (const raw of taps) {
    if (!raw || typeof raw !== 'object') continue
    // The iPhone app leaves an empty value out altogether (Swift's JSON): no `after` is none, no `value` is null.
    const t: CardTap = { ...raw, after: raw.after ?? null, value: raw.value ?? null }
    if (typeof t.key !== 'string' || !UUID.test(t.key)) { if (typeof t.key === 'string') out.rejected.push(t.key); continue }
    // Another account's tap stays in the app for that account.
    if (typeof t.email !== 'string' || t.email.trim().toLowerCase() !== signedInEmail || t.businessId !== partition.businessId) continue
    if (outcomes[t.key]) { out.taken.push(t.key); continue }
    if (queue.some((q) => q.key === t.key)) {
      // Saved in the queue before, its record never written (GPT-6.1's review): written now, before the app is told.
      outcomes[t.key] = { card: true, told: false, at: Date.now() }
      if (saveOutcomes(outcomes)) out.taken.push(t.key)
      continue
    }
    if (inBatch.has(t.key)) continue
    const problem = cardTapProblem(t)
    if (problem && !safeId(t.visitId)) { out.rejected.push(t.key); continue }
    // The change it was made on, as the queue knows it now (C1).
    let version = t.base
    let refused: string | null = problem ? "The lock screen's change couldn't be read" : null
    if (!refused && t.after) {
      const before = queue.find((q) => q.key === t.after) ?? batch.find((q) => q.key === t.after)
      const o = outcomes[t.after]
      // Behind it in the queue: its answer chains this one; behind a refused one, refused too (Sonnet's review).
      if (before) { if (before.status === 'conflict' || before.status === 'failed') refused = "The change before it didn't send" }
      else if (o?.left === 'landed' && typeof o.sent === 'number' && typeof o.returned === 'number') {
        // It landed: made on exactly what it saved, unless that change landed on top of someone else's.
        if (o.returned === o.sent + 1) version = o.returned
      } else refused = "The change before it didn't send"
    }
    // A time with how it was got (0083); a finish taken off (Undo) has none, and the server clears it.
    const how = t.value !== null && (t.how === 'tap' || t.how === 'noticed') ? { [t.field === 'started_at' ? 'started_how' : 'finished_how']: t.how } : {}
    const body = { version, [t.field]: t.value, ...how }
    batch.push({
      key: t.key,
      label: typeof t.label === 'string' && t.label.trim() ? t.label.trim().slice(0, 120) : 'Lock screen',
      method: 'PATCH',
      path: `/api/cleans/${t.visitId}`,
      body,
      status: refused ? 'failed' : 'queued',
      ...(refused ? { error: refused } : {}),
      createdAt: createdAt++,
      claim: null,
      businessId: partition.businessId,
      authorEmail: signedInEmail,
      attempted: false,
      cardAfter: t.after ?? null,
      ...(jobsAwareDefault ? { jobsAware: true } : {}),
      ...(moneyAwareDefault ? { moneyAware: true } : {}),
    })
    inBatch.add(t.key)
  }
  if (batch.length === 0) return out
  queue = orderQueue([...queue, ...batch])
  notify()
  // Every record of the batch saved, or none of it is kept (B7): a Finish must not be held here while its Start is not.
  if (batch.some((m) => localOnly.has(m.key)) || !queuePersistenceOk()) {
    queue = queue.filter((q) => !inBatch.has(q.key))
    for (const k of inBatch) localOnly.delete(k)
    notify()
    return { taken: out.taken, rejected: out.rejected }
  }
  for (const m of batch) outcomes[m.key] = { card: true, told: false, at: Date.now() }
  // The records saved, or nothing of the batch is kept and the app is told nothing (GPT-6.1's review).
  if (!saveOutcomes(outcomes)) {
    queue = queue.filter((q) => !inBatch.has(q.key))
    notify()
    return { taken: out.taken, rejected: out.rejected }
  }
  rememberKeys(batch.map((m) => m.key))
  out.taken.push(...batch.map((m) => m.key))
  void flushQueue()
  return out
}

// The app has forgotten these taps: their records no longer need keeping for it.
export function cardTold(keys: string[]): void {
  const all = jobOutcomes()
  let changed = false
  for (const k of keys) if (all[k]?.card && all[k].told === false) { all[k] = { ...all[k], told: true }; changed = true }
  if (changed) saveOutcomes(all)
}

// Taps taken here that the app has not yet been told about (B1: told again on every start until it answers).
export function cardUntold(): string[] {
  return Object.entries(jobOutcomes()).filter(([, o]) => o.card && o.told === false).map(([k]) => k)
}

// The account the queue is for now, for the card's own records (null: nobody).
export function cardAccount(): { email: string; businessId: number } | null {
  return identified && signedInEmail && partition.ns !== null && partition.businessId !== null ? { email: signedInEmail, businessId: partition.businessId } : null
}

// ---- reads ------------------------------------------------------------------
//
// Reads get the same three guarantees writes already had, because a van in a
// dead zone is the normal case, not the edge case:
//   - a hard timeout (a stalled connection used to hang the screen forever —
//     mutations had SEND_TIMEOUT_MS, GETs had nothing);
//   - a last-good cache, so opening the app with no signal still shows the
//     round instead of a raw `Failed to fetch`;
//   - an `online` listener (in main.ts) that re-reads when signal returns —
//     before, only the write queue woke up and the screen stayed empty.
const READ_TIMEOUT_MS = 12_000
const CACHE_PREFIX = 'dropby-round-cache-v1:'

export interface Cached<T> {
  data: T
  at: number
}

// Cache only the payloads the van needs to read cold. Money is shown from
// cache ONLY behind an explicit "as it was at HH:MM" banner (see ui.loadError)
// — never silently, because a stale unpaid flag is a real-world argument.
// Keys are namespaced per signed-in identity (see setIdentityPartition): the
// business-1 owner keeps the legacy keys, everyone else reads and writes
// only their own block and can never land on — or read — anyone else's.
// Nothing is cached or read back before an identity is known.
export function readCache<T>(key: string): Cached<T> | null {
  if (screensStale) return null // the saved copy is the other account's
  const storageKey = cacheStorageKey(key)
  if (storageKey === null) return null
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Cached<T>
    return typeof parsed?.at === 'number' ? parsed : null
  } catch {
    return null
  }
}

// A saved copy made from a larger answer (Today's five-day window split into its days, docs/plans/offline/
// PLAN-v2.md §3): stored exactly as a read with that cache key would have stored it.
export function saveCache(key: string, data: unknown): void {
  writeCache(key, data)
}

// The same save, saying whether it is really on the phone (false: storage full or off, or nobody signed in). The
// "this phone has stored these days" message (screens/today.ts, migration 0038) names only days that were: a day
// that failed to save must keep the owner's "hasn't downloaded" line (Astra, 1 Oct).
export function storeCache(key: string, data: unknown): boolean {
  return writeCache(key, data)
}

function writeCache(key: string, data: unknown): boolean {
  const storageKey = cacheStorageKey(key)
  if (storageKey === null) return false
  try {
    localStorage.setItem(storageKey, JSON.stringify({ data, at: Date.now() }))
    return true
  } catch {
    // storage full/off — the cache is a bonus, never a requirement
    return false
  }
}

// A successful read proves the Access session is alive again, so every
// mutation parked on 'signed-out' goes back in the queue and flushes. This is
// the ONLY thing that releases them: the user signs in, the app reads, the
// held edits save themselves.
// When the most recent hold was recorded. A read that was already in flight
// BEFORE the session expired proves nothing about it — releasing on that
// answer sent the held edit straight back into the login page.
let signedOutAt = 0

function resumeAfterSignIn(startedAt: number): void {
  if (startedAt < signedOutAt) return
  if (!queue.some((q) => q.status === 'signed-out')) return
  queue = queue.map((q) => (q.status === 'signed-out' ? { ...q, status: 'queued' as const, error: undefined, claim: null } : q))
  notify()
  void flushQueue()
}

// A refused or unanswered read. The message is what the screens always
// showed; `status` (0 = no answer) and the parsed JSON `body` let a caller
// read the server's reason, which is how loadMe tells "not a Roundly user"
// from "this business is paused". The body's own fields also ride on the
// error itself ({ status, ...body }: err.code, err.email, err.canSignUp),
// never over the message, status or body.
export class ApiError extends Error {
  readonly status: number
  readonly body: Record<string, unknown> | null
  readonly code: string | undefined
  constructor(message: string, status: number, body: Record<string, unknown> | null = null) {
    super(message)
    for (const [field, value] of Object.entries(body ?? {})) {
      if (!(field in this)) (this as unknown as Record<string, unknown>)[field] = value
    }
    this.status = status
    this.body = body
    this.code = typeof body?.code === 'string' ? body.code : undefined
  }
}

export async function api<T>(path: string, cacheKey?: string): Promise<T> {
  const startedAt = Date.now()
  // A business's data is only ever asked for on behalf of the business signed
  // in, and says so in the header; with no business, it is not asked at all.
  const businessId = partition.businessId
  if (!mayRequest(path, businessId)) throw new ApiError(NO_BUSINESS_READ, 0)
  if (screensStale && path.split('?')[0] !== '/api/me') throw new ApiError(IDENTITY_CHANGED_MESSAGE, 0)
  // Restored with no signal: no business data is asked for (the screens fall back to their saved copy); only
  // /api/me, which is how the phone finds out the signal is back and who is signed in.
  if (localOnlyMode && path.split('?')[0] !== '/api/me') throw new ApiError(NO_SIGNAL, 0)
  const gen = partition.gen
  const headers = businessHeader(path, businessId)
  let res: Response
  try {
    res = await fetch(path, 'X-Roundly-Business' in headers
      ? { headers, signal: AbortSignal.timeout(READ_TIMEOUT_MS) }
      : { signal: AbortSignal.timeout(READ_TIMEOUT_MS) })
  } catch (err) {
    // Timeout and offline both land here; both mean "no answer", not "bad edit".
    throw new ApiError((err as Error)?.name === 'TimeoutError' ? 'timed out' : 'no connection', 0)
  }
  // Same disguise as on the write path: Access answers a 200 HTML login page,
  // which used to surface as a raw JSON parse error the offline wording did
  // not recognise, so the phone blamed the signal for an expired session.
  // Roundly's own sign-in answers with a reason code, which /api/me reads to
  // show the sign-in page at boot.
  if (res.status === 401 && res.headers.get('X-Roundly-Signed-Out') === '1') throw new ApiError(SIGNED_OUT_MESSAGE, 401, { code: 'signed_out' })
  if (isAccessLoginPage(res)) throw new Error(SIGNED_OUT_MESSAGE)
  // Answered after the phone switched accounts: the previous account's data,
  // neither shown nor cached. (/api/me is exempt: it is how the switch is
  // found out, and loadMe orders overlapping checks itself.)
  const stale = (): boolean => gen !== partition.gen && path.split('?')[0] !== '/api/me'
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    let body: Record<string, unknown> | null = null
    try {
      const parsed: unknown = await res.json()
      if (isObject(parsed)) body = parsed
      if (typeof body?.error === 'string' && body.error) msg = body.error
    } catch {
      // keep generic message
    }
    if (stale()) throw new ApiError(IDENTITY_CHANGED_MESSAGE, 0)
    const err = new ApiError(msg, res.status, body)
    // "Not the account you think": find out who is signed in now.
    if (isBusinessHold(res.status, err.code) && path.split('?')[0] !== '/api/me') requestIdentityRecheck()
    throw err
  }
  const data = (await res.json()) as T
  if (stale()) throw new ApiError(IDENTITY_CHANGED_MESSAGE, 0)
  if (cacheKey) writeCache(cacheKey, data)
  resumeAfterSignIn(startedAt)
  return data
}

// A file the server makes (an invoice's PDF, 26 Oct 2026): read as api() reads, the same business header, timeout,
// signed-out and switched-account handling, but its bytes rather than JSON, and never cached (Astra's PDF check 8).
export async function apiBlob(path: string): Promise<Blob> {
  const businessId = partition.businessId
  if (!mayRequest(path, businessId)) throw new ApiError(NO_BUSINESS_READ, 0)
  if (screensStale) throw new ApiError(IDENTITY_CHANGED_MESSAGE, 0)
  if (localOnlyMode) throw new ApiError(NO_SIGNAL, 0)
  const gen = partition.gen
  let res: Response
  try {
    res = await fetch(path, { headers: businessHeader(path, businessId), signal: AbortSignal.timeout(READ_TIMEOUT_MS) })
  } catch (err) {
    throw new ApiError((err as Error)?.name === 'TimeoutError' ? 'timed out' : 'no connection', 0)
  }
  if (res.status === 401 && res.headers.get('X-Roundly-Signed-Out') === '1') throw new ApiError(SIGNED_OUT_MESSAGE, 401, { code: 'signed_out' })
  if (isAccessLoginPage(res)) throw new Error(SIGNED_OUT_MESSAGE)
  if (gen !== partition.gen) throw new ApiError(IDENTITY_CHANGED_MESSAGE, 0)
  if (!res.ok) {
    let body: Record<string, unknown> | null = null
    try { const parsed: unknown = await res.json(); if (isObject(parsed)) body = parsed } catch { /* keep generic */ }
    const err = new ApiError(typeof body?.error === 'string' && body.error ? body.error : `HTTP ${res.status}`, res.status, body)
    if (isBusinessHold(res.status, err.code)) requestIdentityRecheck()
    throw err
  }
  const blob = await res.blob()
  if (gen !== partition.gen) throw new ApiError(IDENTITY_CHANGED_MESSAGE, 0)
  return blob
}

// A POST that needs its answer now or not at all (a Help question): never
// queued for later like a change, same business header, same signed-out and
// switched-account handling as a read. Throws ApiError on any failure.
export async function postNow<T>(path: string, body: unknown): Promise<T> {
  const businessId = partition.businessId
  if (!mayRequest(path, businessId)) throw new ApiError(NO_BUSINESS_READ, 0)
  if (screensStale) throw new ApiError(IDENTITY_CHANGED_MESSAGE, 0)
  if (localOnlyMode) throw new ApiError(NO_SIGNAL, 0)
  const gen = partition.gen
  let res: Response
  try {
    res = await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...businessHeader(path, businessId) },
      body: JSON.stringify(body ?? {}),
      signal: AbortSignal.timeout(READ_TIMEOUT_MS),
    })
  } catch (err) {
    throw new ApiError((err as Error)?.name === 'TimeoutError' ? 'timed out' : 'no connection', 0)
  }
  if (isAccessLoginPage(res)) throw new Error(SIGNED_OUT_MESSAGE)
  let parsed: unknown = null
  try {
    parsed = await res.json()
  } catch {
    parsed = null
  }
  if (gen !== partition.gen) throw new ApiError(IDENTITY_CHANGED_MESSAGE, 0)
  if (!res.ok) {
    const b = isObject(parsed) ? parsed : null
    throw new ApiError(typeof b?.error === 'string' && b.error ? b.error : `HTTP ${res.status}`, res.status, b)
  }
  return parsed as T
}

// A change that needs its answer now or not at all (My days, 0040: booking a day a week ahead is only right if
// the server says yes now; one kept for later could land inside the week, or on a day someone has since taken).
// One Idempotency-Key per intent, never queued. A caller may keep its key for an unconfirmed send.
// Throws ApiError on any failure, with the server's own words.
export async function changeNow<T>(method: 'POST' | 'PATCH' | 'DELETE', path: string, body: unknown, key: string = crypto.randomUUID()): Promise<T> {
  const businessId = partition.businessId
  if (!mayRequest(path, businessId)) throw new ApiError(NO_BUSINESS_READ, 0)
  if (screensStale) throw new ApiError(IDENTITY_CHANGED_MESSAGE, 0)
  if (localOnlyMode) throw new ApiError(NO_SIGNAL, 0)
  const gen = partition.gen
  let res: Response
  try {
    res = await fetch(path, {
      method,
      headers: { 'content-type': 'application/json', 'Idempotency-Key': key, ...businessHeader(path, businessId) },
      body: JSON.stringify(body ?? {}),
      signal: AbortSignal.timeout(READ_TIMEOUT_MS),
    })
  } catch (err) {
    throw new ApiError((err as Error)?.name === 'TimeoutError' ? 'timed out' : 'no connection', 0)
  }
  if (isAccessLoginPage(res)) throw new Error(SIGNED_OUT_MESSAGE)
  let parsed: unknown = null
  try {
    parsed = await res.json()
  } catch {
    parsed = null
  }
  if (gen !== partition.gen) throw new ApiError(IDENTITY_CHANGED_MESSAGE, 0)
  if (!res.ok) {
    const b = isObject(parsed) ? parsed : null
    throw new ApiError(typeof b?.error === 'string' && b.error ? b.error : `HTTP ${res.status}`, res.status, b)
  }
  return parsed as T
}

// ---- boot ---------------------------------------------------------------------
// The queue itself is NOT loaded here any more: loadMe() calls
// setIdentityPartition() as soon as /api/me answers, and only then is this
// identity's namespace adopted and flushed. Until then the retry timer and
// the online signal are deliberately idle — sending the previous user's
// queued edits before knowing who signed in is the leak this prevents.

window.addEventListener('online', () => void flushQueue())
setInterval(() => void flushQueue(), RETRY_MS)

